window.utils = window.utils || {};

var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
if (isSafari) document.documentElement.setAttribute('data-safari', true);

window.utils.throttle = (callback, limit) => {
  let waiting = false; // Initially, we're not waiting
  return function () {
    if (!waiting) {
      callback.apply(this, arguments);
      waiting = true;
      setTimeout(function () {
        waiting = false;
      }, limit);
    }
  };
};

window.utils.announce = (text, el) => {
  if (!window.LRTimer) {
    window.LRTimer = {};
  }

  let element = el || document.getElementById('liveRegion');
  let id = element.getAttribute('id');

  element.textContent = text;

  if (window.LRTimer[id]) {
    clearTimeout(window.LRTimer[id]);
  }

  window.LRTimer[id] = setTimeout(() => {
    element.textContent = '';
  }, 300);
};

window.utils.isInViewport = (el) => {
  const rect = el.getBoundingClientRect();
  const html = document.documentElement;
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || html.clientHeight) &&
    rect.right <= (window.innerWidth || html.clientWidth)
  );
};

window.utils.debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

new ResizeObserver(() => {
  let vw = document.documentElement.clientWidth / 100;
  document.documentElement.style.setProperty('--vw', `${vw}px`);
}).observe(document.documentElement);

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

function onIntersection(elements, observer) {
  elements.forEach((element) => {
    const elementTarget = element.target;
    const originColorScheme = elementTarget.getAttribute('data-color-scheme-original');
    const transitionColorScheme = elementTarget.getAttribute('data-color-scheme-transition');

    if (element.isIntersecting) {
      if (!elementTarget.classList.contains('scroll-trigger--visible')) {
        elementTarget.classList.add('scroll-trigger--visible');

        if (transitionColorScheme) {
          elementTarget.setAttribute('data-color-scheme', transitionColorScheme);
        }
      }
    } else {
      elementTarget.classList.remove('scroll-trigger--visible');

      if (transitionColorScheme) {
        elementTarget.setAttribute('data-color-scheme', originColorScheme);
      }
    }
  });
}

function initScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName('scroll-trigger'));
  if (animationTriggerElements.length === 0) return;

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '-5% 0px -15% 0px',
  });

  animationTriggerElements.forEach((element) => observer.observe(element));
}

function onVideoIntersection(elements, observer) {
  elements.forEach((element) => {
    const elementTarget = element.target;
    const isVideoLoaded = elementTarget.getAttribute('data-loaded');
    const videoElement = elementTarget.querySelector('video');

    if (element.isIntersecting) {
      if (!isVideoLoaded) {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        elementTarget.loadContent();
      } else {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        elementTarget.play();
      }
    } else {
      elementTarget.pause();
    }
  });
}

function initVideoAutoplayTrigger() {
  const videoElements = Array.from(document.querySelectorAll('[data-autoplay="true"]'));

  const observer = new IntersectionObserver(onVideoIntersection, {
    rootMargin: '0px 0px 0px 0px',
  });

  videoElements.forEach((element) => observer.observe(element));
}

function labelExternalLinks() {
  const links = document.querySelectorAll('a[target="_blank"]');
  links.forEach((link) => link.setAttribute('aria-describedby', 'a11y-new-window-message'));
}

window.addEventListener('DOMContentLoaded', () => {
  initScrollAnimationTrigger();
  initVideoAutoplayTrigger();
  labelExternalLinks();
});

function pauseAllMedia(container = document) {
  container.querySelectorAll('deferred-media, external-media').forEach((video) => {
    if (video.pause) {
      video.pause();
    }
  });
  container.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.playPause = this.querySelector('play-pause-button');
    const poster = this.querySelector('[id^="Deferred-Media-Poster"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    // window.pauseAllMedia();
    if (!this.getAttribute('data-loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('data-loaded', true);
      this.media = content.querySelector('video, model-viewer, iframe');
      const img = this.media.querySelector('img:not([alt])');
      img?.setAttribute('alt', '');

      if (this.media.nodeName === 'VIDEO') {
        this.media.load();
        this.media.oncanplay = () => {
          this.appendChild(this.media);
          this.play();
        };
      } else {
        this.appendChild(this.media);
      }

      if (focus) {
        if (this.playPause) this.playPause.focus();
        this.media.focus();
      }
      this.addEventListeners(this.media);
    }
  }

  addEventListeners = (media) => {
    media.addEventListener('play', () => {
      this.setAttribute('data-playing', true);
      this.playPause.setAttribute('data-playing', true);
    });

    media.addEventListener('pause', () => {
      this.setAttribute('data-playing', false);
      this.playPause.setAttribute('data-playing', false);
    });

    const button = this.querySelector('play-pause-button');
    if (button) {
      button.addEventListener('click', () => {
        if (media.paused) {
          this.play();
        } else {
          this.pause();
        }
      });
    }
  };

  play() {
    if (this.media?.tagName === 'VIDEO') {
      this.media.play();
    }
  }

  pause() {
    if (this.media?.tagName === 'VIDEO') {
      this.media.pause();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class PlayPauseButton extends HTMLElement {
  static observedAttributes = ['data-playing'];
  constructor() {
    super();
  }

  connectedCallback() {
    this.ready(this.init);
  }

  ready(fn) {
    if (this.children.length) {
      return fn.apply(this);
    }
    new MutationObserver(fn.bind(this)).observe(this, { childList: true });
  }

  init() {
    this.dataLabel = this.querySelector('[data-label]');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-playing' && this.dataLabel) {
      if (newValue === 'true') {
        this.dataLabel.innerHTML = this.getAttribute('data-pause');
      } else if (newValue === 'false') {
        this.dataLabel.innerHTML = this.getAttribute('data-play');
      }
    }
  }
}

customElements.define('play-pause-button', PlayPauseButton);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.mounted = false;
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.pageWidth = document.documentElement.clientWidth;
    this.timer = null;

    this.grid = this.querySelector('.slider-component__grid');
    this.progressBar = this.querySelector('[data-progress]');
    this.controls = this.querySelector('.slider-component__controls');
    this.countElement = this.querySelector('.slider-counter');
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelectorAll('[data-direction="previous"]');
    this.nextButton = this.querySelectorAll('[data-direction="next"]');
    this.animationStyle = this.getAttribute('data-animation-style');
    this.singleMode = this.tagName === 'SLIDESHOW-COMPONENT';

    if (!this.slider || !this.nextButton) return;
    if (this.sliderItems.length <= 1) return this.hideSlideshow();

    if (this.animationStyle === 'fade') {
      this.sliderItems.forEach((item) => {
        if (item.getAttribute('aria-hidden') != 'true') {
          this.currentPage = parseInt(item.dataset.index) + 1 || 1;
        }
      });
    } else {
      this.currentPage = 1;
    }

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => {
      if (this.sliderWidth === this.slider.clientWidth) {
        return;
      }
      this.initPages();
    });

    resizeObserver.observe(this.slider);

    this.slider.scrollTo(0, 0, { behavior: 'instant' });

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
    this.nextButton.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });

    if (this.dataset.destroy) {
      this.resizeObserver = new ResizeObserver((elements, observer) => {
        this.checkDestroy();
      });
      this.resizeObserver.observe(document.documentElement);
    }
  }

  checkDestroy() {
    if (this.width === window.innerWidth) {
      return;
    }
    this.width = window.innerWidth;
    const mediaQuery = this.dataset.destroy;
    if (mediaQuery && window.matchMedia(mediaQuery).matches) {
      this.hideSlideshow(true);
    } else {
      this.unhideSlideshow();
    }
  }

  initPages() {
    const slideComputedStyle = getComputedStyle(this.slider);
    const columns = parseInt(slideComputedStyle.getPropertyValue('--columns'));
    this.limit = this.singleMode ? this.sliderItems.length : this.sliderItems.length - columns + 1;
    const styleMatch = slideComputedStyle.gridTemplateColumns.match(/[0-9]\d*(\.\d+)?/g);
    this.sliderWidth = styleMatch
      ? styleMatch.slice(1).reduce((a, b) => parseFloat(a) + parseFloat(b), 0)
      : this.clientWidth;
    let widthNeeded, gap, columnWidth, containerWidth;
    if (this.grid) {
      containerWidth = this.grid.clientWidth;
      const gapStyle = getComputedStyle(this.grid).gap;
      if (gapStyle) {
        gap = Number(gapStyle?.match(/[0-9]\d*(\.\d+)?/g)[1] || gapStyle?.match(/[0-9]\d*(\.\d+)?/g)[0]);
        columnWidth = (containerWidth - (columns - 1) * gap) / columns;
        widthNeeded = (this.sliderItems.length - 1) * gap + columnWidth * this.sliderItems.length;
      }
    }
    this.pageOffsetLeft = styleMatch ? Number(styleMatch[0]) : 0;
    this.sliderItemOffset = Math.abs(this.sliderItems[1]?.offsetLeft - this.sliderItems[0].offsetLeft);
    this.slidesPerPage = this.singleMode ? 1 : (this.sliderWidth - this.pageOffsetLeft) / this.sliderItemOffset;
    if (widthNeeded < this.sliderWidth) {
      this.totalPages = 1;
    } else if (this.singleMode) {
      this.totalPages = this.limit;
      const slide = this.sliderItems[this.currentPage - 1];
      this.jumpToSlide(slide, true);
    } else {
      const itemsThatFitOnSide = Math.floor(this.pageOffsetLeft / (columnWidth + gap));
      this.totalPages = this.limit - itemsThatFitOnSide;
    }

    this.update(false);
    this.pageTotalElement.setAttribute('data-total', this.totalPages);
    this.pageTotalElement.textContent = this.totalPages;
  }

  update(announcement) {
    let slidePadding;
    if (this.grid) {
      const slideComputedStyle = getComputedStyle(this.grid);
      const sliderPaddingStr = slideComputedStyle.paddingLeft.replace('px', '');
      if (sliderPaddingStr) slidePadding = parseInt(sliderPaddingStr);
    }

    this.pageTotalElement.textContent = this.totalPages;

    if (this.totalPages <= 1) {
      this.hideSlideshow();
    } else {
      this.checkDestroy();
    }

    if (!this.singleMode) {
      let currentPageIndex;
      this.sliderItems.forEach((item, index) => {
        const focusableElements = getFocusableElements(item);

        if (this.isSlideVisible(item)) {
          if (currentPageIndex === undefined) {
            currentPageIndex = index;
          }
          item.setAttribute('aria-hidden', 'false');
          item.removeAttribute('tabindex');
          focusableElements.forEach((el) => {
            el.getAttribute('tabindex') ? el.setAttribute('tabindex', 0) : el.removeAttribute('tabindex');
          });
        } else {
          item.setAttribute('aria-hidden', 'true');
          item.setAttribute('tabindex', '-1');
          focusableElements.forEach((el) => {
            el.setAttribute('tabindex', '-1');
          });
        }
      });

      if (currentPageIndex != undefined) {
        this.currentPage = currentPageIndex + 1;
      }
    }

    this.currentPageElement.textContent = Math.min(this.currentPage, this.totalPages) || 1;

    if (this.currentPage === 1 && !this.enableSliderLooping) {
      this.prevButton.forEach((button) => button.setAttribute('aria-disabled', true));
    } else {
      this.prevButton.forEach((button) => button.removeAttribute('aria-disabled', true));
    }

    if (this.currentPage === this.totalPages && !this.enableSliderLooping) {
      this.nextButton.forEach((button) => button.setAttribute('aria-disabled', true));
    } else {
      this.nextButton.forEach((button) => button.removeAttribute('aria-disabled'));
    }

    this.updateProgressBar();
    if (announcement) {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.announcePosition.bind(this), 300);
    }
  }

  updateProgressBar() {
    if (this.progressBar) {
      let dividend, divisor;
      if (!this.singleMode) {
        dividend = Math.abs(this.slider.scrollLeft) + this.slider.clientWidth;
        divisor = this.slider.scrollWidth;
      } else {
        dividend = this.currentPage;
        divisor = this.totalPages;
      }

      let progress = Math.min((dividend / divisor) * 100, 100);
      this.progressBar.style.setProperty('--progress', `${progress}%`);
      if (this.progressBar.classList.contains('faded-out'))
        setTimeout(() => this.progressBar.classList.remove('faded-out'), 0);
    }
  }

  hideSlideshow(destroyed) {
    this.controls?.setAttribute('hidden', true);
    this.slider.removeAttribute('tabindex');
    this.slider.removeAttribute('role');
    this.slider.removeAttribute('aria-label');
    this.classList.add('slider--collapsed');
    this.sliderItems.forEach((item) => {
      item.removeAttribute('aria-hidden');
      item.removeAttribute('role');
    });
    if (destroyed) {
      this.setAttribute('data-destroyed', true);
    }
  }

  unhideSlideshow() {
    this.setup();
    if (!this.classList.contains('slider--collapsed')) return;
    this.removeAttribute('data-destroyed');
    this.classList.remove('slider--collapsed');
  }

  setup() {
    let setUpFade = false;
    const unhidden = this.slider.querySelectorAll("[aria-hidden='false']");
    if (unhidden.length > 1) {
      setUpFade = true;
    }
    this.controls?.removeAttribute('hidden');
    this.slider.setAttribute('tabindex', 0);
    this.slider.setAttribute('role', 'region');
    const label = this.dataset.label;
    if (document.getElementById(label)) {
      this.slider.setAttribute('aria-labelledby', label);
    } else {
      this.slider.setAttribute('aria-label', window.accessibilityStrings.sliderLabel);
    }
    this.sliderItems.forEach((item, index) => {
      item.setAttribute('data-index', index);
      if (this.singleMode) {
        item.setAttribute(
          'aria-label',
          window.accessibilityStrings.slideLabel
            .replace('{{ count }}', index + 1)
            .replace('{{ total }}', this.sliderItems.length)
        );
        item.setAttribute('role', 'group');
      }
      if (this.animationStyle !== 'fade') {
        if (this.isSlideVisible(item)) {
          item.removeAttribute('aria-hidden');
        } else {
          item.setAttribute('aria-hidden', true);
        }
      } else if (setUpFade && this.animationStyle === 'fade') {
        if (index != 0) {
          item.setAttribute('aria-hidden', true);
        } else {
          item.setAttribute('aria-hidden', false);
        }
      }
    });
  }

  announcePosition() {
    if (this.slider.getAttribute('data-slider-autoplay') === 'true' && !this.autoplayButtonIsSetToPlay) return;
    window.utils.announce(this.countElement.innerText.replace('/', ''));
  }

  onButtonClick(event) {
    if (event.currentTarget.getAttribute('aria-disabled') === 'true') {
      return;
    }

    const direction = event.currentTarget.dataset.direction;
    this.calculateFromDirection(direction);
  }

  calculateFromDirection(direction, jump = true) {
    if (direction === 'next') {
      if (this.currentPage === this.sliderItems.length) {
        if (!this.enableSliderLooping) return;
        this.currentPage = 1;
      } else {
        this.currentPage = this.currentPage + 1;
      }
    } else if (direction === 'previous') {
      if (this.currentPage === 1) {
        if (!this.enableSliderLooping) return;
        this.currentPage = this.sliderItems.length;
      } else {
        this.currentPage = this.currentPage - 1;
      }
    }
    if (jump) this.jumpToSlide(this.sliderItems[this.currentPage - 1]);
  }

  isSlideVisible(element) {
    if (this.singleMode && this.animationStyle === 'fade') {
      return this.currentPage === parseInt(element.dataset.index) + 1;
    } else {
      const rect = element.getBoundingClientRect();
      if (rect.left < -1 || rect.right < -1) {
        return false;
      }
      if (rect.right > this.clientWidth) {
        return false;
      }

      return true;
    }
  }

  setSlidePosition(position, skipAnimation = false) {
    this.slider.scrollTo({
      left: position,
      behavior: skipAnimation ? 'instant' : 'smooth',
    });
  }

  jumpToSlide(slide, skipAnimation) {
    if (!slide) return;
    if (document.dir === 'rtl') {
      this.slideScrollPosition = Math.round(this.clientWidth - slide.clientWidth - slide.offsetLeft) * -1;
    } else {
      this.slideScrollPosition = Math.round(slide.offsetLeft);
    }
    this.setSlidePosition(this.slideScrollPosition, skipAnimation);
  }
}

customElements.define('slider-component', SliderComponent);

class CustomDialog extends HTMLElement {
  constructor() {
    super();
    this.name = this.dataset.name;

    this.elements = {
      toggle: this.querySelector('[data-toggle]'),
      dialog: this.querySelector('dialog'),
      dialogWindow: this.querySelector('.dialog__window'),
      close: this.querySelectorAll('[data-close]'),
    };

    if (!this.querySelector('.dialog__overlay') && this.dataset.allowClickOutside !== 'true') {
      this.overlay = document.createElement('div');
      this.overlay.classList.add('dialog__overlay', 'dialog__animateable');
      this.elements.dialog.prepend(this.overlay);
      this.overlay.addEventListener('click', this.closeDialog.bind(this));
    }

    if (this.elements.toggle) this.elements.toggle.addEventListener('click', this.openDialog.bind(this));
    document.addEventListener(`dialog:trigger:${this.name}`, this.openDialog.bind(this));
    document.addEventListener(`dialog:force-close:${this.name}`, this.closeDialog.bind(this));
    this.elements.dialog.addEventListener(`close`, this.onClose.bind(this));
    this.elements.close.forEach((close) => {
      close.addEventListener('click', this.closeDialog.bind(this));
    });

    this.addEventListener('keydown', this.onKeyDown.bind(this));

    if (this.dataset.delay) {
      const delay = parseInt(this.dataset.delay * 1000);
      if (this.dataset.once) {
        if (localStorage.getItem(`theme:${this.dataset.once}`)) {
          return;
        }
      }
      setTimeout(() => {
        this.returnFocus = document.activeElement;
        this.openDialog();
      }, delay);
    }
  }

  onKeyDown(event) {
    if (event.code.toUpperCase() === 'ESCAPE' && this.dataset.modal != 'true') {
      this.closeDialog();
    }
  }

  openDialog(e) {
    if (Shopify?.designMode === true) {
      this.elements.dialog.setAttribute('open', true);
    } else if (this.dataset.modal === 'true') {
      this.elements.dialog.showModal();
      this.preventBodyScroll();
    } else {
      this.elements.dialog.show();
    }

    const scrollToTop = this.querySelectorAll('.dialog__body, [data-scroll-to-top]');
    scrollToTop.forEach((el) => {
      el.scrollTo(0, 0);
    });

    setTimeout(() => {
      const fade = this.querySelectorAll('[data-animate-fade]');
      fade.forEach((element) => element.classList.remove('faded-out'));
    }, 100);
    const event = new CustomEvent(`dialog:open:${this.name}`, {
      detail: {
        target: this,
      },
    });
    document.dispatchEvent(event);
  }

  closeDialog() {
    if (this.dataset.once) {
      localStorage.setItem(`theme:${this.dataset.once}`, true);
    }

    this.elements.dialog.close();

    const event = new CustomEvent(`dialog:close:${this.name}`, {
      detail: {
        target: this,
      },
    });
    document.dispatchEvent(event);

    if (this.returnFocus) {
      this.returnFocus.focus();
    }
  }

  onClose() {
    if (!document.querySelector('[data-modal="true"] dialog[open]')) this.allowBodyScroll();
    const fade = this.querySelectorAll('[data-animate-fade]');
    fade.forEach((element) => element.classList.add('faded-out'));
  }

  preventBodyScroll() {
    document.body.style.overflowY = 'hidden';
    document.body.style['overscroll-behavior'] = 'none';
  }

  allowBodyScroll() {
    document.body.style.overflowY = '';
    document.body.style['overscroll-behavior'] = '';
  }
}

customElements.define('custom-dialog', CustomDialog);

class LinkToButton extends HTMLElement {
  constructor() {
    super();
    this.link = this.querySelector('a');
    this.init();
  }

  init() {
    this.button = document.createElement('button');
    this.button.innerHTML = this.link.innerHTML;

    [...this.link.attributes].forEach(({ name, value }) => this.button.setAttribute(name, value));
    this.link.replaceWith(this.button);
  }
}

customElements.define('link-to-button', LinkToButton);

class DialogTrigger extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.name = this.dataset.name;
    this.isOpen = false;
    this.body = document.body;

    this.button?.addEventListener('click', () => {
      const event = new CustomEvent(`dialog:trigger:${this.name}`, {
        detail: {
          detail: this.dataset.detail,
        },
      });
      document.dispatchEvent(event);
    });

    // Escuchar eventos de apertura y cierre del diálogo
    document.addEventListener(`dialog:open:${this.name}`, () => {
      this.isOpen = true;
      this.button?.classList.add('is-opening');
      this.body.classList.add('overflow-hidden');
    });

    document.addEventListener(`dialog:close:${this.name}`, () => {
      this.isOpen = false;
      this.button?.classList.remove('is-opening');
      this.body.classList.remove('overflow-hidden');
    });
  }
}

customElements.define('dialog-trigger', DialogTrigger);

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        window.utils
          .debounce((event) => {
            this.onChange(event);
          }, 300)
          .bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.getAttribute('aria-hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.removeAttribute('aria-hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.setAttribute('aria-hidden', true);
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

if (!customElements.get('collapsible-content')) {
  customElements.define(
    'collapsible-content',
    class CollapsibleContent extends HTMLElement {
      constructor() {
        super();
        this.el = this.querySelector('details');
        this.summary = this.querySelector('summary');
        this.content = this.querySelector('[data-content]');
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.summary.addEventListener('click', (e) => this.onClick(e));
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.borderThickness = parseInt(
          window.getComputedStyle(this).getPropertyValue('--border-thickness').replace('px', '')
        );

        this.content.addEventListener('keydown', (e) => {
          if (e.code.toUpperCase() === 'ESCAPE') {
            e.preventDefault();
            e.stopPropagation();
            this.summary.focus();
            this.collapse();
          }
        });

        if (this.dataset.destroy) {
          this.resizeObserver = new ResizeObserver((elements, observer) => {
            this.checkDestroy();
          });
          this.resizeObserver.observe(document.documentElement);
        }
      }

      connectedCallback() {
        this.setupTruncate();
        this.setupCollapseTriggers();
        this.applyDefaultState();
      }

      /**
       * Aplica truncamiento dinámico de texto según configuración responsive en atributos de data.
       * Muestra u oculta un botón para expandir o contraer el texto si supera las líneas permitidas.
       */
      setupTruncate() {
        const truncate = this.getAttribute('data-truncate');
        if (!truncate) return;

        try {
          const {
            enable_truncate,
            truncate_visibility,
            truncate_chars_desktop,
            truncate_chars_mobile,
            more_info_text,
            less_info_text
          } = JSON.parse(truncate);

          if (!enable_truncate) return;

          const containerText = this.querySelector('[data-truncate-event]');
          const moreInfoButton = this.querySelector('[data-btn-event]');
          if (!containerText || !moreInfoButton) return;

          // Guardar el texto completo original
          const fullText = containerText.textContent;
          
          // Determinar si debe truncar en base a ancho de pantalla y configuración
          const isMobile = window.innerWidth < 768;
          const visibleInDevice =
            (isMobile && ['mobile', 'both'].includes(truncate_visibility)) ||
            (!isMobile && ['desktop', 'both'].includes(truncate_visibility));

          if (visibleInDevice) {
            const maxChars = isMobile ? truncate_chars_mobile : truncate_chars_desktop;
            
            // Solo truncar si el texto es más largo que el límite
            if (fullText.length > maxChars) {
              // Crear una versión truncada del texto
              const truncatedText = fullText.substring(0, maxChars) + '...';
              
              // Aplicar el texto truncado
              containerText.textContent = truncatedText;
              containerText.dataset.truncated = 'true';
              
              // Mostrar botón para expandir
              moreInfoButton.classList.remove('hidden');
              moreInfoButton.textContent = more_info_text;
            }
          }

          // Alternar entre truncado y texto completo
          moreInfoButton.addEventListener('click', () => {
            const isTruncated = containerText.dataset.truncated === 'true';
            
            if (isTruncated) {
              // Mostrar texto completo
              containerText.textContent = fullText;
              containerText.dataset.truncated = 'false';
              moreInfoButton.textContent = less_info_text;
            } else {
              // Volver a truncar
              const maxChars = isMobile ? truncate_chars_mobile : truncate_chars_desktop;
              const truncatedText = fullText.substring(0, maxChars) + '...';
              
              containerText.textContent = truncatedText;
              containerText.dataset.truncated = 'true';
              moreInfoButton.textContent = more_info_text;
            }
          });
        } catch (err) {
          console.error('Error parsing truncate data:', err);
        }
      }

       /**
       * Configura los disparadores externos que pueden abrir este elemento colapsable.
       * Usa delegación de eventos en el documento para capturar clics en elementos con la clase especificada,
       * incluso cuando estos elementos se añaden dinámicamente al DOM.
       */
       setupCollapseTriggers() {
        const trigger = this.getAttribute('data-trigger-collapse');
        
        if (!trigger) return;
        
        // Guardar referencia al elemento actual para usar dentro del event listener
        const collapsibleElement = this;
        
        // Usar delegación de eventos en el documento
        document.addEventListener('click', function(event) {
          // Verificar si el elemento clicado o alguno de sus padres tiene la clase trigger
          const clickedElement = event.target.closest(`.${trigger}`);
          
          // Si no se encontró un elemento con la clase trigger, no hacer nada
          if (!clickedElement) return;
          
          event.preventDefault();
          
          // Obtener y abrir el elemento details
          const details = collapsibleElement.querySelector('details');
          if (details && !details.hasAttribute('open')) {
            details.setAttribute('open', '');
          }
          
          // Desplazar la vista hacia el elemento colapsable
          collapsibleElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }

      /**
       * Aplica el estado inicial (abierto/cerrado) del colapsable según la configuración
       * responsive definida en los atributos data-default-state-desktop y data-default-state-mobile.
       * Respeta diferentes configuraciones para móvil y escritorio.
       */
      applyDefaultState() {
        if (!this.el) return;

        // Obtener los estados por defecto del elemento padre (div que contiene el collapsible)
        const parentDiv = this.closest('div[data-default-state-desktop], div[data-default-state-mobile]');
        
        if (!parentDiv) return;
        
        const desktopState = parentDiv.dataset.defaultStateDesktop || 'closed';
        const mobileState = parentDiv.dataset.defaultStateMobile || 'closed';
        
        // Determinar si estamos en móvil o desktop
        const isMobile = window.innerWidth < 768;
        
        // Aplicar el estado según el dispositivo
        const shouldBeOpen = isMobile ? mobileState === 'open' : desktopState === 'open';
        
        if (shouldBeOpen && !this.el.hasAttribute('open')) {
          this.el.setAttribute('open', '');
          // Asegurarnos de que la altura se ajuste correctamente
          this.el.style.height = 'auto';
        }
        
        // También escuchar cambios de tamaño de ventana para actualizar el estado
        if (!this._resizeListener) {
          this._resizeListener = window.utils.debounce(() => {
            this.applyDefaultState();
          }, 250);
          
          window.addEventListener('resize', this._resizeListener);
        }
      }

      checkDestroy() {
        if (this.width === window.innerWidth) {
          return;
        }
        this.width = window.innerWidth;
        const mediaQuery = this.dataset.destroy;
        if (mediaQuery && window.matchMedia(mediaQuery).matches) {
          this.hide();
        } else {
          this.unhide();
        }
      }

      hide() {
        this.el.setAttribute('open', true);
        this.el.classList.add('collapsible-item--destroyed');
        this.summary.removeEventListener('click', (e) => this.onClick(e));
      }

      unhide() {
        this.el.removeAttribute('open');
        this.el.classList.remove('collapsible-item--destroyed');
        this.summary.removeAttribute('tabindex', -1);
      }

      onClick(e) {
        e.preventDefault();
        this.el.style.overflow = 'hidden';
        if (this.isClosing || !this.el.open) {
          this.open();
        } else if (this.isExpanding || this.el.open) {
          this.collapse();
        }
      }

      collapse() {
        this.el.style.overflowY = 'hidden';
        this.isClosing = true;
        const startHeight = `${this.el.offsetHeight}px`;
        const endHeight = `${this.summary.offsetHeight + this.borderThickness}px`;

        if (this.animation) {
          this.animation.cancel();
        }

        // Start a WAAPI animation
        if (!this.prefersReducedMotion.matches) {
          this.animation = this.animate([startHeight, endHeight]);

          this.animation.onfinish = () => this.onAnimationFinish(false);
          this.animation.oncancel = () => (this.isClosing = false);
        } else {
          this.onAnimationFinish(false);
        }
      }

      open() {
        this.el.style.height = `${this.el.offsetHeight + this.borderThickness}px`;
        this.el.open = true;
        window.requestAnimationFrame(() => this.expand());
      }

      expand() {
        this.isExpanding = true;
        const startHeight = this.el.style.height;
        const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight + this.borderThickness}px`;

        if (this.animation) {
          this.animation.cancel();
        }
        if (!this.prefersReducedMotion.matches) {
          this.animation = this.animate([startHeight, endHeight]);

          this.animation.onfinish = () => this.onAnimationFinish(true);
          this.animation.oncancel = () => (this.isExpanding = false);
        } else {
          this.onAnimationFinish(true);
        }
      }

      animate(height) {
        return this.el.animate(
          {
            height: height,
          },
          {
            duration: 300,
            easing: 'ease',
          }
        );
      }

      onAnimationFinish(open) {
        this.el.open = open;
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.el.style.height = this.el.style.overflow = '';
      }
    }
  );
}

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.announcementBarSlider = this.classList.contains('announcement-bar__slideshow');
    this.sliderControlWrapper = this.querySelector('.slider-component__controls');
    this.enableSliderLooping = this.slider.dataset.looping === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion.addEventListener('change', () => {
      if (this.slider.getAttribute('data-slider-autoplay') === 'true') this.setAutoPlay();
    });

    if (this.sliderItems.length <= 1) return this.hideSlideshow();

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    this.slider.addEventListener('keyup', this.onKeyUp.bind(this));

    this.setSlideVisibility();

    this.initCustomCursorOnPointer()

    if (this.animationStyle === 'fade') {
      this.touchstartX = 0;
      this.touchendX = 0;
      this.sliderItems.forEach((item) => {
        if (item.querySelector('product-model')) return;

        item.addEventListener('touchstart', (e) => {
          this.touchstartX = e.changedTouches[0].screenX;
        });

        item.addEventListener('touchend', (e) => {
          this.touchendX = e.changedTouches[0].screenX;
          this.onSwipe();
        });
      });
    } else {
      this.slideObserver = new IntersectionObserver(
        (elements, observer) => {
          elements.forEach((element) => {
            if (element.intersectionRatio > 0.5) {
              this.currentPage = parseInt(element.target.dataset.index) + 1;
              this.update();
            }
          });
        },
        {
          threshold: [0.5],
        }
      );
      this.sliderItems.forEach((item) => {
        this.slideObserver.observe(item);
      });
    }

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      [...this.prevButton, ...this.nextButton].forEach((button) => {
        button.addEventListener('click', () => {
          this.announcementBarArrowButtonWasClicked = true;
          setTimeout(() => {
            button.focus();
          }, 500);
        });
      });
    }

    this.observer = new IntersectionObserver(
      (elements, observer) => {
        elements.forEach((element) => {
          if (element.isIntersecting) {
            if (this.slider.getAttribute('data-slider-autoplay') === 'true') this.setAutoPlay();
          } else {
            this.pause();
          }
        });
      },
      {
        threshold: [0, 1],
      }
    );
    this.observer.observe(this);
  }

  onKeyUp(event) {
    if (
      (event.code.toUpperCase() === 'ARROWRIGHT' && document.dir !== 'rtl') ||
      (event.code.toUpperCase() === 'ARROWLEFT' && document.dir === 'rtl')
    ) {
      this.calculateFromDirection('next');
    } else if (
      (event.code.toUpperCase() === 'ARROWLEFT' && document.dir !== 'rtl') ||
      (event.code.toUpperCase() === 'ARROWRIGHT' && document.dir === 'rtl')
    ) {
      this.calculateFromDirection('previous');
    }
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('[data-autoplay-control]')) {
      this.sliderAutoplayButton = this.querySelector('play-pause-button');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.sliderAutoplayButton.setAttribute('data-playing', true);
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    if (event.currentTarget.getAttribute('aria-disabled') === 'true') {
      return;
    }
    super.onButtonClick(event);

    this.wasClicked = true;
    this.calculateFade();
    if (!this.enableSliderLooping) return;

    if (this.announcementBarSlider) this.pause();
  }

  calculateFade() {
    if (this.animationStyle === 'fade') {
      this.sliderItems.forEach((el, index) => {
        if (this.currentPage === index + 1) {
          el.setAttribute('aria-hidden', false);
        } else {
          el.setAttribute('aria-hidden', true);
        }
      });
      this.update();
      this.setSlideVisibility();
    }
  }

  onSwipe() {
    const swipeRight = this.touchendX < this.touchstartX && this.touchstartX - this.touchendX > 50;
    const swipeLeft = this.touchendX > this.touchstartX && this.touchendX - this.touchstartX > 50;
    if ((swipeRight && document.dir !== 'rtl') || (swipeLeft && document.dir === 'rtl')) {
      this.calculateFromDirection('next');
    } else if ((swipeLeft && document.dir !== 'rtl') || (swipeRight && document.dir === 'rtl')) {
      this.calculateFromDirection('previous');
    }
  }

  calculateFromDirection(direction, jump) {
    super.calculateFromDirection(direction, jump);
    this.calculateFade();
  }

  setSlidePosition(position, skipAnimation = false) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
        behavior: skipAnimation ? 'instant' : 'smooth',
      });
    }, this.announcerBarAnimationDelay);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider && this.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.setAttribute('data-playing', false);
    } else {
      this.sliderAutoplayButton.setAttribute('data-playing', true);
    }
  }

  autoRotateSlides() {
    let slideScrollPosition;
    this.calculateFromDirection('next');
    let newPos = this.slider.scrollLeft + this.sliderItemOffset;
    if (document.dir === 'rtl') {
      newPos = this.slider.scrollLeft - this.sliderItemOffset;
    }
    if (this.enableSliderLooping) {
      slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : newPos;
    } else {
      if (this.currentPage === this.sliderItems.length) return;
      slideScrollPosition = newPos;
    }
    this.setSlidePosition(slideScrollPosition);
  }

  setSlideVisibility() {
    this.sliderItems.forEach((item, index) => {
      const focusableElements = getFocusableElements(item);
      if (index === this.currentPage - 1) {
        focusableElements.forEach((el) => {
          el.removeAttribute('tabindex');
        });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
        const autoplay = item.querySelector('[data-autoplay="true"]');
        if (autoplay) {
          setTimeout(() => {
            autoplay.play();
          });
        }
      } else {
        focusableElements.forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
        pauseAllMedia(this);
      }
    });
    this.wasClicked = false;

    if (!document.querySelector("[data-custom-ring]")) return
    this.circleAnimation(this.slider.dataset.speed)
  }

  jumpToSlide(slide, skipAnimation) {
    if (!slide) return;
    super.jumpToSlide(slide, skipAnimation);
    this.currentPage = [...this.sliderItems].findIndex((item) => item === slide) + 1;
    slide.setAttribute('aria-hidden', false);

    this.calculateFade();
    this.setSlideVisibility();
  }

  circleAnimation(duration) {
    const customRing = document.querySelector("[data-custom-ring]");
    const circle = customRing.querySelector("[data-custom-ring] circle");

    
    const customCursor = document.querySelector("[data-custom-cursor]");
    const sliderAutoplay = customCursor.dataset.customCursorAutoplay;
    const isAutoplay = sliderAutoplay === "true";
    
    if (!circle) return;

    if (customCursor.dataset.customCursorAutoplay === "true") {

      const length = circle.getTotalLength();
  
      // Paso 1: reset valores para reiniciar animación
      circle.style.transition = "none"; // sin transición
      circle.style.strokeDasharray = `${length}`;
      circle.style.strokeDashoffset = length;
    
      // Paso 2: forzar reflow para que el navegador aplique el reset
      circle.getBoundingClientRect();
    
      // Paso 3: volver a poner la transición y animar a 0
      circle.style.transition = `stroke-dashoffset ${duration}s ease-out`;
      circle.style.strokeDashoffset = "0";
    } else {
      circle.style.strokeDasharray = "0";
    }
  }

  initCustomCursorOnPointer() {
    const mediaQuery = window.matchMedia('(pointer: fine)');
    let cursorEl = null;
    let cursorRing = null;
    let cursorActive = false;
    let insideComponent = false;
  
    const enableCursor = () => {
      if (!cursorActive) {
        this.setupCustomCursor();
        cursorEl = this.querySelector('[data-custom-cursor]');
        cursorRing = this.querySelector('[data-custom-ring]');
        cursorActive = true;

        if(!cursorEl) return
  
        // Ocultar el cursor custom hasta entrar en el componente
        if (cursorEl) cursorEl.style.opacity = '0';
  
        // Detectar entrada y salida del componente
        this.addEventListener('mouseenter', () => {
          insideComponent = true;
          if (cursorEl) cursorEl.style.opacity = '1';
          document.body.style.cursor = 'none'; // ocultar cursor normal
        });
  
        this.addEventListener('mouseleave', () => {
          insideComponent = false;
          if (cursorEl) cursorEl.style.opacity = '0';
          document.body.style.cursor = ''; // restaurar cursor
        });
  
        // Movimiento del cursor solo dentro del componente
        window.addEventListener('mousemove', (e) => {
          if (insideComponent && cursorEl) {
            cursorEl.style.top = `${e.clientY - cursorEl.offsetHeight / 2}px`;
            cursorEl.style.left = `${e.clientX - cursorEl.offsetWidth / 2}px`;

            const bounds = this.getBoundingClientRect();
            const middleX = bounds.left + bounds.width / 2;

            // Añadir clase según lado
            if (e.clientX < middleX) {
              cursorEl.classList.add('is-left');
              cursorEl.classList.remove('is-right');
            } else {
              cursorEl.classList.add('is-right');
              cursorEl.classList.remove('is-left');
            }
          }
        });
      }
    };
  
    const disableCursor = () => {
      if (cursorActive) {
        if (cursorEl) cursorEl.remove();
        cursorActive = false;
      }
    };
  
    const checkPointer = () => {
      const customCursor = document.querySelector("[data-custom-cursor]");

      if(!customCursor) return

      if (mediaQuery.matches) {
        enableCursor();
      } else {
        disableCursor();
      }
    };
  
    // Comprobar al inicio
    checkPointer();
  
    // Cambios de tipo de puntero
    mediaQuery.addEventListener('change', checkPointer);
  
    // También en resize
    window.addEventListener('resize', checkPointer);
  } 

  setupCustomCursor() {
    const cursor = this.querySelector('[data-custom-cursor]'); // dentro del web component

    if(!cursor) return
    
    window.addEventListener('mousemove', (e) => {
      cursor.style.top = `${e.clientY - cursor.offsetHeight / 2}px`;
      cursor.style.left = `${e.clientX - cursor.offsetWidth / 2}px`;
    });
  
    window.addEventListener('mouseleave', () => {
      cursor.style.opacity = '0';
    });
  
    window.addEventListener('mouseenter', () => {
      cursor.style.opacity = '1';
    });
  
    // Escalado al pasar sobre enlaces o botones
    this.querySelectorAll('a, button').forEach(el => {
      el.addEventListener('mouseenter', () => {
        cursor.style.transform = 'scale(1.5)';
      });
      el.addEventListener('mouseleave', () => {
        cursor.style.transform = 'scale(1)';
      });
    });
  
    // Escalado al presionar y soltar el mouse
    window.addEventListener('mousedown', () => {
      cursor.style.transform = 'scale(.2)';
    });
  
    window.addEventListener('mouseup', () => {
      cursor.style.transform = 'scale(1)';
    });
  }
}
customElements.define('slideshow-component', SlideshowComponent);

class VariantPicker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (e) => {
      const target = e.target.tagName === 'SELECT' ? e.target.selectedOptions[0] : e.target;
      //this.updateSelectionMetadata(e);

      const event = new CustomEvent('option-value:change', {
        detail: {
          target,
          selectedOptionValues: this.selectedOptionValues,
          focusId: e.target.id,
        },
      });
      document.dispatchEvent(event);
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');
    }
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-picker', VariantPicker);
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.input.addEventListener('input', this.updateQuantityInputWidth.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
    this.updateQuantityInputWidth();
  }

  connectedCallback() {
    this.validateQtyRules();
    document.addEventListener('quantity-update', this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('quantity-update', this.validateQtyRules.bind(this));
  }

  onInputChange(event) {
    this.validateQtyRules();
    this.updateQuantityInputWidth();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }

    window.utils.announce(this.input.value);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector("[name='minus']");
      if (parseInt(value) <= parseInt(this.input.min)) {
        buttonMinus.setAttribute('aria-disabled', true);
      } else {
        buttonMinus.removeAttribute('aria-disabled');
      }
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector("[name='plus']");
      if (value >= max) {
        buttonPlus.setAttribute('aria-disabled', true);
      } else {
        buttonPlus.removeAttribute('aria-disabled');
      }
    }
  }

  updateQuantityInputWidth() {
    this.style.setProperty('--quantity-input-character-count', `${this.input.value.length}ch`);
  }
}

customElements.define('quantity-input', QuantityInput);

class QuickAddButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.button.addEventListener('click', this.onButtonClick.bind(this));
    document.addEventListener(`dialog:quickadd-loaded`, this.onQuickAddLoaded.bind(this));
  }

  onButtonClick() {
    const event = new CustomEvent(`dialog:quickadd`, {
      detail: {
        url: this.dataset.url,
      },
    });
    document.dispatchEvent(event);
    this.button.classList.add('loading');
  }

  onQuickAddLoaded() {
    this.button.classList.remove('loading');
  }
}

customElements.define('quick-add-button', QuickAddButton);

class QuickAddModal extends CustomDialog {
  constructor() {
    super();
    this.dialogWindow = this.querySelector('.dialog__window');
    this.body = this.querySelector('[data-body]');
    this.header = this.querySelector('[data-header]');
    document.addEventListener(`dialog:quickadd`, this.loadQuickAdd.bind(this));
  }

  loadQuickAdd(event) {
    fetch(`${event.detail.url}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const page = html.querySelector('product-page');
        const colorScheme = page.closest('[data-color-scheme]').dataset.colorScheme;
        const id = page.closest('.shopify-section').getAttribute('id');
        this.body.id = id;
        this.updateHTML(page, colorScheme);
        const event = new CustomEvent(`dialog:quickadd-loaded`);
        document.dispatchEvent(event);
      })
      .catch((e) => console.error(e));
  }

  updateHTML(page, colorScheme) {
    const mediaScheme = page.dataset.mediaScheme;
    const mediaButton = page.dataset.mediaButton;
    this.header.setAttribute('data-color-scheme', mediaScheme);
    const button = this.header.querySelector('[data-close]');
    button.classList.remove(...['button--text', 'button--primary', 'button--secondary']);
    button.classList.add(`button--${mediaButton}`);

    page.removeAttribute('data-update-url');
    page.querySelector('[data-destroy]')?.removeAttribute('data-destroy');
    const removeElements = page.querySelectorAll('[data-quick-add-remove]');
    removeElements.forEach((el) => {
      el.remove();
    });
    const window = this.querySelector('.dialog__window');
    window.setAttribute('data-color-scheme', colorScheme);
    if (!page.querySelector('.media-gallery--mobile-max')) {
      this.header.classList.add('quick-add__dialog-header--absolute');
    }

    const scripts = page.querySelectorAll('script');
    const scriptEls = [];
    scripts.forEach((script) => {
      const src = script.getAttribute('src');
      if (src && !document.querySelector(`script[src="${src}"]`)) {
        const scriptEl = document.createElement('script');
        scriptEl.src = src;
        scriptEls.push(scriptEl);
      }
      script.remove();
    });

    const replaceDescription = page.querySelector('[data-description]');
    const replaceEl = page.querySelector('[data-replace-description]');
    if (replaceDescription && replaceEl) {
      replaceEl.innerHTML = replaceDescription.innerHTML;
    }

    const removeContainer = page.querySelector('.product-info__container');
    if (removeContainer) {
      removeContainer.classList.forEach((cls) => {
        if (cls.startsWith('container--')) {
          removeContainer.classList.remove(cls);
        }
      });
    }

    this.body.replaceChildren(page);
    scriptEls.map((el) => {
      document.body.appendChild(el);
    });
    this.openDialog();
    this.dialogWindow.scrollTo(0, 0);
    const media = page.querySelectorAll('[data-autoplay="true"]');
    media.forEach((media) => {
      media.loadContent();
    });
  }
}

customElements.define('quick-add-modal', QuickAddModal);

class ErrorModal extends CustomDialog {
  constructor() {
    super();
    this.body = this.querySelector('[data-body]');
    document.addEventListener(`cart:error`, this.onError.bind(this));
    document.addEventListener(`rendering:error`, this.onRenderingError.bind(this));
  }

  onError(event) {
    if (Array.isArray(event.detail.errors)) {
      this.body.innerHTML =
        Object.keys(event.detail.errors)
          .map((key, index) => {
            return event.detail.errors[key];
          })
          .join('. ') + '.';
    } else {
      this.body.innerHTML = event.detail.message;
    }
    this.openDialog();
  }

  onRenderingError() {
    this.body.innerHTML = window.accessibilityStrings.generalError;
    this.openDialog();
  }
}

customElements.define('error-modal', ErrorModal);

const isYoutubeLoaded = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});

class ExternalMedia extends DeferredMedia {
  constructor() {
    super();
    this.type = this.dataset.host;
  }

  loadContent() {
    super.loadContent((focus = false));
    this.muted = this.dataset.autoplay === 'true';
    return new Promise(async (resolve) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';

      if (this.type === 'youtube') {
        if (!window.YT || !window.YT.Player) {
          script.src = 'https://www.youtube.com/iframe_api';
          document.head.appendChild(script);
          await new Promise((resolve) => {
            script.onload = resolve;
          });
        }
        await isYoutubeLoaded;
        const iframe = this.querySelector('iframe');
        this.player = new YT.Player(iframe, {
          events: {
            onReady: () => {
              if (this.muted) {
                this.player.mute();
              }
              this.player.playVideo();
              resolve();
            },
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                this.setAttribute('data-playing', 'true');
              } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                this.removeAttribute('data-playing');
              }
            },
          },
        });
      }
      if (this.type === 'vimeo') {
        if (!window.Vimeo || !window.Vimeo.Player) {
          script.src = 'https://player.vimeo.com/api/player.js';
          document.head.appendChild(script);
          await new Promise((resolve) => {
            script.onload = resolve;
          });
        }
        this.player = new Vimeo.Player(this.querySelector('iframe'));
        if (this.muted) {
          this.player.setMuted(true);
        }
        this.player.on('play', () => {
          this.setAttribute('data-playing', 'true');
        });
        this.player.on('pause', () => this.removeAttribute('data-playing'));
        this.player.on('ended', () => this.removeAttribute('data-playing'));
        this.player.play();
        resolve();
      }
    });
  }

  play() {
    if (!this.player) return;
    if (this.type == 'youtube' && typeof this.player.playVideo == 'function') {
      this.player?.playVideo();
    }
    if (this.type == 'vimeo' && typeof this.player.play == 'function') {
      this.player?.play();
    }
  }

  pause() {
    if (!this.player) return;
    if (this.type == 'youtube' && typeof this.player.pauseVideo == 'function') {
      this.player?.pauseVideo();
    }
    if (this.type == 'vimeo' && typeof this.player.pause == 'function') {
      this.player?.pause();
    }
  }
}

customElements.define('external-media', ExternalMedia);

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.variantIdInput.disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

    this.cart = document.querySelector('cart-items');

    this.submitButton = this.querySelector('[type="submit"]');
    if (!this.submitButton) return;
    this.submitButtonText = this.submitButton.querySelector('span');

    if (document.querySelector('.cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    this.hideErrors = this.dataset.hideErrors === 'true';
  }

  connectedCallback() {
    // this.setupPlanetGiftRuleIdForProductForms();
  }

  onSubmitHandler(event) {
    const dataToast = {
      image: event.target.closest('product-form').dataset.image,
      title: event.target.closest('product-form').dataset.title,
    }

    if(dataToast.title){
      this.cart.showInstantCartToast(dataToast);
    }

    event.preventDefault();

    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.handleErrorMessage();

    // Planet gift selector: block the other *enabled* gift buttons while this one's request is in
    // flight, so a fast/simultaneous click can't add more than one gift at once. Buttons already
    // disabled for another reason (sold out, promo threshold not met) are left untouched, so they
    // don't get wrongly re-enabled once this request finishes.
    const giftSelector = this.closest('.c-cart__gift-selector');
    const siblingGiftButtons = giftSelector
      ? [...giftSelector.querySelectorAll('product-form [type="submit"]')].filter(
          (button) => button !== this.submitButton && !button.disabled
        )
      : [];
    siblingGiftButtons.forEach((button) => {
      button.disabled = true;
    });

    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.classList.add('loading');

    const spinner = event.target.querySelector('.loading__spinner');
    const text = event.target.querySelector('.c-cp__quick-add-button__text');
    const icon = event.target.querySelector('.icon--pb-cart');

    if(text){
      text.classList.add('hidden');
    }

    if(icon){
      icon.classList.add('hidden');
    }

    if (spinner) spinner.classList.add('loading');
    if (text) text.classList.add('loading');

    const config = {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
    };

    const formData = new FormData(this.form);

    if (this.cart) {
      formData.append(
        'sections',
        this.cart.getSectionsToRender().map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);
    }

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {

          const errors = response.errors;
          const firstKey = Object.keys(errors)[0];
          const firstMessage = errors[firstKey][0];

          const event = new CustomEvent('cart:error', {
            detail: {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors,
              message: firstMessage || response.description || response.message,
            },
          });
          document.dispatchEvent(event);
          this.handleErrorMessage(response.description);
          const quickAddError = this.submitButton.querySelector('[data-error]');
          if (!quickAddError) {
            const cartDrawer = document.querySelector('cart-items');
            cartDrawer?.refreshCart();
          }
          if (!quickAddError) return;
          this.submitButton.setAttribute('aria-disabled', true);
          quickAddError.classList.remove('hidden');
          this.error = true;
          return;
        } else if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error) {
          const event = new CustomEvent('cart:update', {
            detail: {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            },
          });
          document.dispatchEvent(event);
        }

        this.error = false;
        window.utils.announce(window.accessibilityStrings.itemAdded);

        const event = new CustomEvent(`dialog:force-close:quick-add`);
        document.dispatchEvent(event);

        setTimeout(() => {
          this.cart.renderContents(response, dataToast);
        });
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitButton.classList.remove('loading');

        siblingGiftButtons.forEach((button) => {
          button.disabled = false;
        });

        setTimeout(() => {
          if(spinner) spinner.classList.remove('loading');
          if(text) text.classList.remove('hidden');
          if(icon) icon.classList.remove('hidden');
        }, 100);

        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('[data-error]');
    if (!this.errorMessageWrapper) return;

    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('span');
    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }

  toggleSubmitButton(disable = true, text) {
    if (disable) {
      this.submitButton.setAttribute('aria-disabled', true);
      if (text) this.submitButtonText.textContent = text;
    } else {
      this.submitButton.removeAttribute('aria-disabled');
      this.submitButtonText.textContent = window.variantStrings.addToCart;
    }
  }

  get variantIdInput() {
    return this.form.querySelector('[name=id]');
  }
}

customElements.define('product-form', ProductForm);

// Componente web que gestiona una tarjeta de producto con selección de swatches (colores/variantes), filtros y sliders.
class CardProductSwatch extends HTMLElement {
  constructor() {
    super();
    this.onFilterClick = this.onFilterClick.bind(this);
    this.onSwatchClick = this.onSwatchClick.bind(this);
  }

  connectedCallback() {
    // Referencias a elementos del DOM dentro del componente
    this.productForm = this.querySelector('form');
    this.containerSwatch = this.querySelector('.container-swatch, [data-container-swatches]');
    this.swatches = this.querySelectorAll('[data-swatch]');
    this.allSwatches = this.containerSwatch?.querySelectorAll('.js-product-swatch') || [];
    this.swatchFilters = this.querySelector('[data-swatch-filters]');
    this.productLink = this.querySelector('[data-product-link]');
    this.buttonSwatch = this.querySelector('[data-button-swatch]');
    this.textAvailable = this.buttonSwatch?.getAttribute('data-text-available');
    this.textSoldOut = this.buttonSwatch?.getAttribute('data-text-sold-out');
    this.countElement = this.querySelector('.card-product-swatch__count');
    this.bunnyPoints = this.querySelector('.bunny-points, [data-loyalty]');
    this.arrowLink = this.querySelector('.card-product-swatch__arrow, [data-swatch-arrow]');
    this.variantPicker = this.querySelector('#pb-variant-picker');

    this.swatchesPerRow = 0;
    this.swatchesPerRowExpanded = 0;
    this.showFilterOnExceed = this.getAttribute('data-show-filter-on-exceed') || 11;
    this.minItemsToShowFilters = this.getAttribute('data-min-items-to-show-filters') || 8;

    this.variantPickerInit();
    this.initSliders();         // Inicializa los sliders horizontales
    this.initEventListeners();  // Asigna listeners a los clics relevantes
    
    //setimeout para que se calcule el ancho del contenedor cuando esta en predictive search
    setTimeout(() => { 
      this.initSwatches();        // Calcula y muestra swatches según el ancho
      this.gtmEventsClicked();
    }, 100);
  }

  /** Inicializa los listeners para clics en filtros, swatches y el botón de + */
  initEventListeners() {
    this.addEventListener('click', (event) => {
      if (event.target.classList.contains('filter-btn')) this.onFilterClick(event);
      if (event.target.classList.contains('js-filter-btn')) this.onFilterClick(event)
      if (event.target.classList.contains('js-product-swatch')) this.onSwatchClick(event);
      if (event.target.classList.contains('card-product-swatch__count') || event.target.closest('.card-product-swatch__arrow-link, [data-product-link]')) {
        this.onPlusClick(event);
      }
    });
  }

  /** Configura sliders horizontales con flechas, drag con ratón y barra de progreso */
  initSliders() {
    const sliders = this.querySelectorAll('.slider-scroll');
    const isMobile = window.innerWidth <= 768;

    sliders.forEach(slider => {
      const left = slider.parentElement.querySelector('.slider-arrow.left, [data-button-prev]');
      const right = slider.parentElement.querySelector('.slider-arrow.right, [data-button-next]');
      const progressBar = slider.parentElement.querySelector('.slider-progress-bar, [data-progress-bar]');
      const progressFill = progressBar?.querySelector('.slider-progress-fill, [data-progress-bar-fill]');

      // Scroll con flechas izquierda/derecha
      if (left && right) {
        left.addEventListener('click', () => slider.scrollBy({ left: -slider.offsetWidth, behavior: 'smooth' }));
        right.addEventListener('click', () => slider.scrollBy({ left: slider.offsetWidth, behavior: 'smooth' }));
      }

      // Desabilitado drag para que no falle en desktop, en mobile si funciona sin esta función, testeado en simulador.

      // En móvil solo deshabilitamos el drawer
      // if (isMobile) {
      //   slider.style.touchAction = 'none';
      // } else {
      //   // Drag horizontal con ratón solo en desktop
      //   let isDown = false, startX, scrollLeft;
      //   slider.addEventListener('mousedown', (e) => {
      //     isDown = true;
      //     slider.classList.add('dragging');
      //     startX = e.pageX - slider.offsetLeft;
      //     scrollLeft = slider.scrollLeft;
      //     e.preventDefault();
      //   });
      //   slider.addEventListener('mouseleave', () => isDown = false);
      //   slider.addEventListener('mouseup', () => slider.classList.remove('dragging'));
      //   slider.addEventListener('mousemove', (e) => {
      //     if (!isDown) return;
      //     e.preventDefault();
      //     const x = e.pageX - slider.offsetLeft;
      //     const walk = (x - startX) * 1.2;
      //     slider.scrollLeft = scrollLeft - walk;
      //   });
      // }
    // Efecto hover para mostrar segunda imagen
    /* const sliderImgs = slider.querySelectorAll('.slider-img');
    
    // Solo implementamos el efecto si hay al menos 2 imágenes
    if (sliderImgs.length >= 2) {
      sliderImgs.forEach((sliderImg, index) => {
        // Saltamos la última imagen ya que no tendría una siguiente
        if (index < sliderImgs.length - 1) {
          const currentImg = sliderImg.querySelector('img');
          const nextSliderImg = sliderImgs[index + 1];
          const nextImg = nextSliderImg.querySelector('img');
          
          if (currentImg && nextImg) {
            // Guardamos las URLs originales
            const currentImgSrc = currentImg.src;
            const nextImgSrc = nextImg.src;
            
            // Añadimos eventos de hover
            sliderImg.addEventListener('mouseenter', () => {
              // Al hacer hover, cambiamos a la segunda imagen
              currentImg.src = nextImgSrc;
            });
            
            sliderImg.addEventListener('mouseleave', () => {
              // Al quitar el hover, volvemos a la primera imagen
              currentImg.src = currentImgSrc;
            });
          }
        }
      });
    } */

      // Barra de progreso del scroll
      if (progressFill) {
        const images = slider.querySelectorAll('.slider-img, [data-gallery-media]');
        const totalImages = images.length;
        const minPercent = totalImages > 0 ? 100 / totalImages : 100;

        const updateProgressBar = () => {
          const maxScroll = slider.scrollWidth - slider.clientWidth;
          const percent = maxScroll > 0 ? (slider.scrollLeft / maxScroll) * (100 - minPercent) + minPercent : 100;
          progressFill.style.width = percent + '%';
        };

        slider.addEventListener('scroll', updateProgressBar);
        setTimeout(updateProgressBar, 50);
      }
    });
  }

  /** 
 * Calcula y actualiza el número de swatches que caben por fila 
 * basándose en el ancho del contenedor y el ancho individual del swatch
 */
  /**
 * Calcula y actualiza cuántos swatches caben por fila,
 * teniendo en cuenta el espacio ocupado por el contador y la flecha.
 */
  calculateSwatchesPerRow() {
    if (!this.containerSwatch || !this.allSwatches.length) return;
  
    const firstSwatch = this.allSwatches[0];
  
    // Medimos el ancho real del swatch, incluso si está oculto
    const originalDisplay = firstSwatch.style.display;
    firstSwatch.style.visibility = 'hidden';
    firstSwatch.style.display = 'block';
    const swatchWidth = firstSwatch.offsetWidth;
    firstSwatch.style.display = originalDisplay;
    firstSwatch.style.visibility = 'visible';
  
    // Obtenemos el ancho total del contenedor
    const containerWidth = this.containerSwatch.offsetWidth;

    // Obtenemos el gap (espacio entre swatches)
    const computedStyles = window.getComputedStyle(this.containerSwatch);
    const gap = parseInt(computedStyles.columnGap || computedStyles.gap || 0);
    this.swatchesPerRowExpanded = Math.max(0, Math.floor(containerWidth / (swatchWidth + gap)));

    // Obtenemos el ancho reservado para elementos adicionales
    const countEl = this.countElement;

    // Ajustamos el ancho total de cada swatch sumando el gap
    const swatchTotalWidth = swatchWidth + gap;

    this.swatchesPerRow = Math.max(0, Math.floor(containerWidth / swatchTotalWidth));
   
  }
  


  /** Calcula cuántos swatches caben por fila y muestra los visibles inicialmente */
  initSwatches() {
    if (!this.containerSwatch || !this.allSwatches.length) return;

   
    this.calculateSwatchesPerRow();

    // Actualizamos el contador de swatches ocultos
    let hiddenCount = this.allSwatches.length;
    if(this.swatchesPerRow <= this.allSwatches.length) {
      hiddenCount = this.allSwatches.length - this.swatchesPerRow;
    }else if(this.swatchesPerRow > this.allSwatches.length){
      hiddenCount = 0;
    }

    // Mostramos solo los swatches que caben en la primera fila
    if (!this.containerSwatch.classList.contains('first-expanded')) {
      if(hiddenCount > 0){
        let printSwatch = this.swatchesPerRow  - 1;
        hiddenCount++
       
        this.allSwatches.forEach((swatch, i) => {
         
          swatch.style.display = i < printSwatch ? 'inline-flex' : 'none';
        });
      }else if(hiddenCount === 0){
        this.allSwatches.forEach((swatch, i) => {
          swatch.style.display = 'inline-flex';
        });
      }
    }

     if (this.countElement && hiddenCount > 1) {
       this.countElement.textContent = `+${hiddenCount}`;
       this.countElement.classList.remove('hidden');
     }
 
  }

  updateLinks(){
    const urlPdp = this.querySelector('input:checked + label')?.getAttribute('data-product-url');
    this.countElement?.setAttribute('href', urlPdp);
    this.arrowLink?.setAttribute('href', urlPdp);
  }

  /** Muestra más swatches o redirige al PDP si ya están expandidos */
  onPlusClick(e) {
    if (!this.containerSwatch.classList.contains('expanded')){
      e.preventDefault();
      this.updateLinks();
    };

    if (!this.containerSwatch) return;

    this.containerSwatch.classList.add('first-expanded');
    if (this.allSwatches.length > this.showFilterOnExceed && this.swatchFilters) {
      const allFilters = this.swatchFilters.querySelectorAll('.filter-btn, [data-filter]');
      let totalFiltersActive = 0;
    
      allFilters.forEach(filter => {
        const dataFilter = filter.getAttribute('data-filter');
        // Contamos cuántos swatches coinciden con el filtro actual o con "all"
        const count = [...this.allSwatches].filter(swatch => {
          const filters = swatch.getAttribute('data-filters').split(',');
          return filters.includes(dataFilter) || dataFilter === 'all';
        }).length;
    
        // Si hay suficientes coincidencias, mostramos el filtro
        if (count >= this.minItemsToShowFilters) {
          filter.style.display = 'inline';
          totalFiltersActive++;
        }
      });
    
      // Mostramos los filtros solo si hay más de uno activo
      if (totalFiltersActive > 1) {
        this.swatchFilters.classList.remove('hidden');
      }
    }
    

    // Muestra hasta dos filas de swatches, swatchesPerRowExpanded nos dice cuantas swacth entran completamente en la fila
    let showSwatches = (this.swatchesPerRow * 2) - 2;

    let remainingHidden = this.allSwatches.length - showSwatches;
    if(remainingHidden === 1){
      showSwatches++;
      remainingHidden--;
    }

    this.allSwatches.forEach((swatch, i) => {
      if (i < showSwatches) swatch.style.display = 'inline-flex';
    });

    if (this.countElement) {
      this.countElement.textContent = remainingHidden > 0 ? `+${remainingHidden}` : '';
      if(remainingHidden > 0){
        this.countElement.classList.remove('hidden');
      }else{
        this.countElement.classList.add('hidden');
      }
      
      if (this.arrowLink && remainingHidden > 1) {
        this.arrowLink.classList.remove('hidden')
        // old
        // this.arrowLink.style.display = 'block'; 
      }
    }

    if (this.bunnyPoints) {
      this.bunnyPoints.classList.remove('hidden')
      // old cards
      // this.bunnyPoints.style.display = 'block'; 
    }
    this.containerSwatch.classList.add('expanded');
  }

  /** Filtra swatches según el filtro seleccionado (por color, etc.) */
  onFilterClick(event) {
    this.swatchFilters?.querySelectorAll('.filter-btn, [data-filter]').forEach(btn => btn.classList.remove('active'));
    const {target} = event;
    target.classList.add('active');
    const filter = target.getAttribute('data-filter');
    let anyCheckedVisible = false;
    let firstVisibleRadio = null;
    let firstVisibleLabel = null;

    this.swatches.forEach(swatch => {
      const filters = swatch.getAttribute('data-filters').split(',');
      const isVisible = filters.includes(filter) || filter === 'all';
      swatch.style.display = isVisible ? 'block' : 'none';

      // Verificamos si hay alguno seleccionado visible
      const radio = swatch.previousElementSibling;
      if (radio?.type === 'radio') {
        if (isVisible) {
          if (!firstVisibleRadio) {
            firstVisibleRadio = radio;
            firstVisibleLabel = swatch;
          }
          if (radio.checked) anyCheckedVisible = true;
        }
      }
    });

    // Si el seleccionado quedó oculto, seleccionamos el primero visible
    if (!anyCheckedVisible && firstVisibleLabel) {
      firstVisibleLabel.click();
    }

    this.adjustSwatches(); // Recalcula qué swatches se muestran
  }

  /** Ajusta la cantidad de swatches visibles tras un filtro */
  adjustSwatches() {
    if (!this.containerSwatch) return;

    this.allSwatches = Array.from(this.containerSwatch.querySelectorAll('.card-product-swatch, .js-product-swatch'))
      .filter(swatch => swatch.style.display !== 'none');

    //const row = this.containerSwatch.classList.contains('first-expanded') ? 2 : 1;
    this.calculateSwatchesPerRow();

    const showSwatches = (this.swatchesPerRow * 2) - 2;
    this.allSwatches.forEach((swatch, i) => {
      swatch.style.display = i < showSwatches ? 'inline-flex' : 'none';
    });

    const remainingHidden = this.allSwatches.length - showSwatches;
    if (this.countElement) {
      this.countElement.textContent = remainingHidden > 0 ? `+${remainingHidden}` : '';

      if(remainingHidden > 0){
        this.countElement.classList.remove('hidden');
      }else{
        this.countElement.classList.add('hidden');
      }
      
      if (this.arrowLink && remainingHidden > 1) {
        this.arrowLink.style.display = 'block';
      }else{
        this.arrowLink.style.display = 'none';
      }
    }

    this.containerSwatch.classList.add('expanded');
  }

  /** Reemplaza los elementos visuales del producto según la variante seleccionada */
  updateProduct(url) {
    const refs = [
      'data-reference-slider',
      'data-product-title',
      /* 'data-product-price',
      'data-product-discounted', */
      'data-product-form',
      'data-reference-belowBadges'
    ];

    const urlFetch = (url.includes('?')) ? `${url}&section_id=card-swatch-single` : `${url}?section_id=card-swatch-single`;

    fetch(urlFetch)
      .then(res => res.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        refs.forEach(ref => {
          const newEl = doc.querySelector(`[${ref}]`);
         
          const curEl = this.querySelector(`[${ref}]`);
         
          if (newEl && curEl) {
            if(ref === 'data-reference-slider'){
              const hasButtonSlider = curEl.querySelectorAll('[data-button-next]');
              if(hasButtonSlider.length === 0){
                // Remove all buttons from new slider if current slider has no buttons
                newEl.querySelectorAll('[data-button-next], [data-button-prev]').forEach(button => button.remove());
              }
            }

            curEl.replaceWith(newEl);
            this.initSliders(); // Reinit sliders en nuevo contenido
            this.removeGtmEventsClicked();
            this.gtmEventsClicked();
          }
        });
        
        this.gtmEventsListViewed();
        this.updateLinks();
      })
      .catch(err => console.error('Error:', err));
  }

  /** Actualiza los inputs ocultos del formulario con el ID de variante seleccionado */
  updateProductForm(id) {
    const idInput = this.productForm?.querySelector('[name="id"]');
    const productIdInput = this.productForm?.querySelector('[name="product-id"]');
    if (idInput) idInput.value = id;
    if (productIdInput) productIdInput.value = id;
  }

  /** Actualiza el enlace al PDP del producto */
  updateProductUrl(event) {
    const url = event.target.getAttribute('data-product-url');
    if (url) this.productLink.href = url;
  }

  /** Handler al hacer clic en un swatch: actualiza imagen, form y enlace */
  onSwatchClick(event) {
    const swatch = event.target;
    const variantId = swatch.getAttribute('data-variant');
    const productUrl = swatch.getAttribute('data-product-url');

    this.updateProduct(productUrl);
    this.updateProductForm(variantId);
    this.updateProductUrl(event);
  }

  getDataGtm(){
    const variantId = this.querySelector('[data-reference-slider]')?.getAttribute('data-variant');
    const allProductCards = Array.from(document.querySelectorAll('[data-gtm-event="product_clicked"]'));
    const index = allProductCards.indexOf(this);
    return {
      variantId,
      index: index + 1
    }
  }

  //events gtm
  gtmEventsClicked() {
    // Creamos un objeto para guardar los listeners si no existe
    if (!this._gtmListeners) this._gtmListeners = new Map();
  
    const anchors = this.querySelectorAll('a');
    anchors.forEach(anchor => {
      // Creamos la función del listener y la guardamos
      const clickHandler = (e) => {
        //pausamos el evento para pruebas sin quenos recargar la pagina
        //e.preventDefault();

        const {variantId, index} = this.getDataGtm();
        
        document.dispatchEvent(new CustomEvent('product_clicked', {
          detail: {
            variantId,
            index: index + 1
          }
        }));        
      };
  
      anchor.addEventListener('click', clickHandler);
      this._gtmListeners.set(anchor, clickHandler); // Guardamos la referencia
    });
  }
  
  removeGtmEventsClicked() {
    if (!this._gtmListeners) return;
  
    this._gtmListeners.forEach((handler, anchor) => {
      anchor.removeEventListener('click', handler);
    });
  
    this._gtmListeners.clear(); // Limpia el mapa
  }

  gtmEventsListViewed() {
    const {variantId, index} = this.getDataGtm();
    
    if(variantId && index){
      document.dispatchEvent(new CustomEvent('product_list_viewed', {
        detail: {
          variantId,
          index: index + 1
        }
      }));
    }
  }

  variantPickerInit() {
    if(this.variantPicker){
      this.variantPicker.addEventListener('change', (e) => {
        //console.log('varinat change')
        const target = e.target;
        const variantId = this.variantPicker.querySelector('fieldset input:checked').dataset.optionValueId;

        if(variantId){
          const urlBase = this.variantPicker.getAttribute('data-product-url');
          // Verificar si la URL ya tiene parámetros y usar el separador correcto
          const separator = urlBase.includes('?') ? '&' : '?';
          const url = `${urlBase}${separator}variant=${variantId}`;
          this.updateProduct(url);
        }
      });
    }
  }
  
}

customElements.define('card-product-swatch', CardProductSwatch);

class ProductSwatches extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.contentSwatch = this.querySelector('.content-swatch, [data-container-swatches]');
    this.allSwatches = this.contentSwatch?.querySelectorAll('.product-swatch, .js-product-swatch') || [];
    this.swatchFilters = this.querySelector('[data-swatch-filters]');
    this.swatches = this.querySelectorAll('[data-swatch]');
    this.labels = this.querySelectorAll('.product-swatch, .js-product-swatch');
    this.minItemsToShowFilters = 8;

    // Agrega listeners a cada swatch (label)
    this.labels.forEach(label => {
      label.addEventListener('click', e => this.productChange(e));
    });

    // Escucha click en botones de filtro (color, etc.)
    this.addEventListener('click', event => {
      if (event.target.classList.contains('js-filter-btn')) {
        this.onFilterClick(event);
      }
    });
    this.showFilters();
  }

  showFilters() {
    const allFilters = this.swatchFilters.querySelectorAll('.js-filter-btn');
    let totalFiltersActive = 0;
  
    allFilters.forEach(filter => {
      const dataFilter = filter.getAttribute('data-filter');
      // Contamos cuántos swatches coinciden con el filtro actual o con "all"
      const count = [...this.allSwatches].filter(swatch => {
        const filters = swatch.getAttribute('data-filters').split(',');
        return filters.includes(dataFilter) || dataFilter === 'all';
      }).length;
      // Si hay suficientes coincidencias, mostramos el filtro
      if (count >= this.minItemsToShowFilters) {
        filter.classList.remove('hidden');
        filter.style.display = 'inline'
        totalFiltersActive++;
      }
    });
  
    // Mostramos los filtros solo si hay más de uno activo
    if (totalFiltersActive > 1) {
      this.swatchFilters.classList.remove('hidden');
      this.swatchFilters.style.display = 'flex'
    }
  }

  /** Cambia el producto al hacer clic en un swatch */
  productChange(e) {
    const url = e.target.dataset.productUrl;
    const variantId = e.target.dataset.variant;
    const section = this.closest('.shopify-section').id;
    const sectionId = section.replace('shopify-section-', '');

    const refs = [
      'data-product-slider',
      'data-refence-details',
      'data-product-content',
      // Puedes descomentar si se necesitan más elementos:
      // 'data-product-title',
      // 'data-product-price',
      // 'data-product-discounted',
      // 'data-product-form',
      // 'data-reference-belowBadges'
    ];

    const urlFetch = url.includes('?')
      ? `${url}&section_id=${sectionId}`
      : `${url}?section_id=${sectionId}`;

    // Static so it survives this element being replaced; only the latest click may update the page
    ProductSwatches.latestChangeId = (ProductSwatches.latestChangeId || 0) + 1;
    const changeId = ProductSwatches.latestChangeId;

    fetch(urlFetch)
      .then(res => res.text())
      .then(html => {
        if (changeId !== ProductSwatches.latestChangeId) return;

        const sectionElement = document.querySelector(`#${section}`);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const variantId = doc.querySelector('product-swatches').getAttribute('data-variant');

        // Reemplaza los elementos actuales con los nuevos desde el HTML fetch
        refs.forEach(ref => {
          const newEl = doc.querySelector(`[${ref}]`);
          const curEl = sectionElement.querySelector(`[${ref}]`);
          if (newEl && curEl) curEl.replaceWith(newEl);
        });

        this.updateURL(url, variantId);
        this.gtmEventsListViewed(variantId);
        // Only after main succeeded, so the sections below never show a different product than main
        this.refreshDependentSections(url);
      })
      .catch(err => console.error('Error al cambiar de producto:', err));

    // Synctrack EDD
    const productHandle = url.split("/").pop()
    fetch(window.Shopify.routes.root + 'products/'+ productHandle + '.js')
      .then(response => response.json())
      .then(product => {
        window.estimatedCurrentProduct = product
        window.estimatedProductVariants = product.variants
        product.variants.forEach(variant => {
          window.estimatedProductVariantQuantity[variant.id] = variant.inventory_quantity
          window.estimatedProductVariantInventoryPolicy[variant.id] = variant.inventory_policy
        });
        if(product.selected_or_first_available_variant){
          window.estimatedSelectedVariant = {
          id: product.selected_or_first_available_variant.id,
          price: product.selected_or_first_available_variant.price,
          sku: product.selected_or_first_available_variant.sku,
          inventory_quantity: product.selected_or_first_available_variant.inventory_quantity,
          inventory_policy:  product.selected_or_first_available_variant.inventory_policy
        }
        }
        setTimeout(() => {
          window.estimatedHandleAutoSelectCountry?.()
        }, 1000);
      })
  }

  /** Re-renders sections outside main that read product metafields (before/after, ingredients) */
  refreshDependentSections(url) {
    const wrappers = [...document.querySelectorAll('.shopify-section.js-product-dependent-section')];
    if (!wrappers.length) return;

    // Static so it survives this element being replaced by the main section swap
    ProductSwatches.dependentSectionsController?.abort();
    const controller = new AbortController();
    ProductSwatches.dependentSectionsController = controller;

    const sectionIds = wrappers.map(wrapper => wrapper.id.replace('shopify-section-', ''));
    const fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('sections', sectionIds.join(','));

    fetch(fetchUrl, { signal: controller.signal })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(sections => {
        if (controller.signal.aborted) return;

        const loadedScripts = new Set(
          [...document.scripts].map(script => script.getAttribute('src')).filter(Boolean)
        );

        wrappers.forEach((wrapper, index) => {
          const html = sections[sectionIds[index]];
          if (typeof html !== 'string') return;

          const newWrapper = new DOMParser().parseFromString(html, 'text/html').getElementById(wrapper.id);
          if (!newWrapper) return;

          wrapper.replaceChildren(...newWrapper.childNodes);
          this.loadNewScripts(wrapper, loadedScripts);
        });
      })
      .catch(err => {
        if (err.name !== 'AbortError') console.error('Error updating product sections:', err);
      });
  }

  /** Parsed <script> tags never execute; recreate the ones the page hasn't loaded yet */
  loadNewScripts(container, loadedScripts) {
    container.querySelectorAll('script[src]').forEach(inertScript => {
      const src = inertScript.getAttribute('src');
      if (loadedScripts.has(src)) return;

      loadedScripts.add(src);
      const script = document.createElement('script');
      [...inertScript.attributes].forEach(attr => script.setAttribute(attr.name, attr.value));
      inertScript.replaceWith(script);
    });
  }

  async gtmEventsListViewed (variantId) {
    if(variantId){
      document.dispatchEvent(new CustomEvent('product_viewed_custom', {
        detail: {
          variantId,
        }
      }));
      setTimeout(() => {
        window.estimatedReInitApp?.()
      }, 1000);
      setTimeout(() => {
        window.estimatedReInitCountdown?.()
      }, 2000);
    }
  }

  /** Actualiza la URL sin recargar la página */
  updateURL(productUrl, variantId) {
    if (!productUrl) return;

    const currentUrl = new URL(window.location.href);
    const searchParams = new URLSearchParams(currentUrl.search);
    const newUrl = new URL(productUrl, window.location.origin);

    // Mantén los parámetros existentes excepto section_id
    searchParams.forEach((value, key) => {
      if (key !== 'section_id') {
        newUrl.searchParams.set(key, value);
      }
    });

    if(variantId){
      newUrl.searchParams.set('variant', variantId);
    }

    window.history.replaceState({}, '', newUrl.toString());
  }

  /** Filtra los swatches por tipo (color, etc.) */
  onFilterClick(event) {
    this.swatchFilters?.querySelectorAll('.js-filter-btn')
      .forEach(btn => btn.classList.remove('active'));

    const { target } = event;
    target.classList.add('active');

    const filter = target.getAttribute('data-filter');

    let anyCheckedVisible = false;
    let firstVisibleRadio = null;
    let firstVisibleLabel = null;

    this.swatches.forEach(swatch => {
      const filters = swatch.getAttribute('data-filters').split(',');
      const isVisible = filters.includes(filter) || filter === 'all';
      swatch.style.display = isVisible ? 'flex' : 'none';

      const radio = swatch.previousElementSibling;
      if (radio?.type === 'radio') {
        if (isVisible) {
          if (!firstVisibleRadio) {
            firstVisibleRadio = radio;
            firstVisibleLabel = swatch;
          }
          if (radio.checked) anyCheckedVisible = true;
        }
      }
    });

    // Si el swatch seleccionado quedó oculto, seleccionamos el primero visible
    if (!anyCheckedVisible && firstVisibleLabel) {
      firstVisibleLabel.click();
      this.updateLinks();
    }
  }
}

customElements.define('product-swatches', ProductSwatches);

class ToneSearchNavigation extends HTMLElement {
  constructor() {
    super();
    this.jsonSearch = null;    // Datos de tonos obtenidos del JSON
    this.debounceTime = 500;  // Tiempo de espera para debounce (ms)
    this.debounceTimer = null; // Temporizador para debounce
    this.blurTimer = null;    // Temporizador para evento blur
    this.sectionId = null;
  }

  // Método que se ejecuta automáticamente cuando el componente se añade al DOM
  connectedCallback() {
    //traducciones
    this.translateSearch = this.getAttribute('data-translate-search');
    this.translateSearchDigits = this.getAttribute('data-translate-search-digits');
    this.translateSeeAll = this.getAttribute('data-translate-see-all');
    this.translateNoResults = this.getAttribute('data-translate-no-results');
    this.translateProducts = this.getAttribute('data-translate-products');
    this.translateProduct = this.getAttribute('data-translate-product');
    this.translateTone = this.getAttribute('data-translate-tone');
    this.translateThisToneNoProducts = this.getAttribute('data-translate-tone-no-products');
    this.translateSeeProduct = this.getAttribute('data-translate-see-product');
    this.dataSearch = this.querySelector('[data-search]');
    this.dataFamily = this.querySelector('[data-family]');
    this.dataTabEvent = this.querySelector('[data-tab-event="search"]');
    
    this.initTabs();           // Inicializa los botones para cambiar de pestaña
    this.initUrlClicks();      // Inicializa los botones de selección de familia
    this.fetchDataTone();      // Carga los tonos relacionados a la familia activa
    this.fetchSearchTone();    // Carga el JSON de tonos para la búsqueda
    this.initSearchInput();    // Inicializa la búsqueda por código de tono
  }

  /**
   * Inicializa el comportamiento de las pestañas (familias / búsqueda).
   * Permite cambiar entre las dos vistas principales del componente.
   */
  initTabs() {
    const buttons = this.querySelectorAll('[data-tab-event]');
    const families = this.querySelector('.families-container');
    this.sectionId = this.getAttribute('data-section-id');
    const search = this.querySelector(`.search-container-${this.sectionId}`);

    // Verificar que existan los elementos necesarios
    if (!buttons.length || !families || !search) {
      console.warn('Elementos de pestañas no encontrados');
      return;
    }

    buttons.forEach(button => {
      button.addEventListener('click', () => {
        // Activar el botón seleccionado y desactivar los demás
        buttons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');

        // Mostrar solo el contenedor de la pestaña seleccionada
        const isFamily = button.dataset.tabEvent === 'families';
        families.classList.toggle('active', isFamily);
        search.classList.toggle('active', !isFamily);
      });
    });
  }

  /**
   * Inicializa los botones que cambian la familia de tonos seleccionada.
   * Al hacer clic, actualiza la familia activa y carga sus datos.
   */
  initUrlClicks() {
    const buttons = this.querySelectorAll('[data-click-event]');

    buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        const parent = e.target.closest('.c-navPills');
        // Quitar clase activa del botón actual
        const current = parent.querySelector('.c-navPills__item--active');

        if (current) {
          current.classList.remove('c-navPills__item--active')
        };

        // Activar el nuevo botón seleccionado
        button.classList.add('c-navPills__item--active');
        
        // Cargar los datos de la familia seleccionada
        this.fetchDataTone(parent);
      });
    });
  }

  /**
   * Carga HTML de tonos para una familia seleccionada desde el servidor.
   * Actualiza el color base según la familia seleccionada.
   */
  fetchDataTone(parent = null) {
    let active = null;
    if (!parent) {
      active = this.querySelector('[data-url].c-navPills__item--active');
    } else {
      active = parent.querySelector('.c-navPills__item--active');
    }

    if (!active) return;

    const { url, baseColor, baseColorName } = active.dataset;

    // if (baseColorName && baseColorName.toLowerCase().includes('iconicos')) {

    const variants = ['iconicos', 'icónicos', 'iconico', 'icónico'];

    if ( baseColorName && variants.some(v => baseColorName.toLowerCase().includes(v.toLowerCase()))) {
      this.classList.add('iconic-family');
    } else {
      this.classList.remove('iconic-family');
    }

    // Actualiza el color base global del componente
    // this.querySelector(`#style-color-${this.sectionId}`).innerHTML = `.tone-search-${this.sectionId} { --base-color: ${baseColor}; }`;

    this.querySelector(`#style-color-${this.sectionId}`).innerHTML = `.tone-search-${this.sectionId} { --base-color: ${baseColor}; --family-box-bg-color-rgb: ${this.hexToRgba(baseColor, 0.1)} }`;


    const fetchUrl = `${window.location.origin}${url}?section_id=tone-search-navigation`;

    fetch(fetchUrl)
      .then(res => res.ok ? res.text() : Promise.reject(`Error HTTP: ${res.status}`))
      .then(html => this.processDataTone(html))
      .catch(err => console.error('Error al obtener datos de familia de tonos:', err));
  }

  /**
   * Procesa el HTML recibido y actualiza el contenido de la familia de tonos.
   * @param {string} html - HTML con los datos de la familia de tonos
   */
  processDataTone(html) {
    if (!html?.trim()) {
      console.warn('Respuesta vacía al obtener HTML de familia');
      return;
    }

    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-content-event]');
      const current = this.querySelector('[data-content-event]');
      
      if (newContent && current) {
        current.innerHTML = newContent.innerHTML;
        this.activeBoxFamily();
      } else {
        console.warn('No se pudo actualizar el contenido de familia de tonos');
      }
    } catch (err) {
      console.error('Error al procesar HTML de tono:', err);
    }
  }

  /**
   * Carga los datos JSON con todos los tonos disponibles desde la sección dedicada.
   */
  fetchSearchTone() {
    const query = `
    {
      metaobjects(type: "swatches", first: 250) {
        edges {
          node {
            id
            fields {
              key
              value
              reference {
                ... on Collection {
                  id
                  title
                  description
                  handle
                  subtitle: metafield(namespace: "custom", key: "subtitle") { value }
                  products(first: 30) {
                    edges {
                      node {
                        title
                        handle
                        images(first: 1) {
                          edges {
                            node {
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    `;

    // TODO: Cambiar el token para que se pueda modificar desde el setting
    fetch('/api/2025-10/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': 'e719177bf43518ac50abb59201794639'
        //'X-Shopify-Storefront-Access-Token': 'c93d9a1e4792e1a18d3bbb27c4113a1a'
      },
      body: JSON.stringify({ query })
    })
      .then(res => res.json())
      .then(data => {
        // Procesa la respuesta aquí
        const tones = data.data.metaobjects.edges.map(edge => {
          // Convierte el array fields en un objeto { key: value }
          const fieldsObj = Object.fromEntries(
            edge.node.fields.map(f => [f.key, f.value])
          );
        
          // Busca la referencia a la colección (si existe)
          const collectionField = edge.node.fields.find(f => f.key === 'collection');
          const collectionRef = collectionField && collectionField.reference;
        
          return {
            label: fieldsObj.label,
            tone_code: fieldsObj.tone_code,
            color: fieldsObj.color,
            collection: collectionRef
              ? {
                  url: `/collections/${collectionRef.handle}`,
                  title: collectionRef.title,
                  description: collectionRef.description,
                  handle: collectionRef.handle,
                  subtitle: collectionRef.subtitle?.value,
                  products_count: collectionRef.products.edges.length,
                  products: collectionRef.products.edges.map(prodEdge => ({
                    title: prodEdge.node.title,
                    handle: prodEdge.node.handle,
                    url: `/products/${prodEdge.node.handle}`,
                    image: prodEdge.node.images?.edges?.length > 0 ? prodEdge.node.images.edges[0].node.url : null,
                    // Agrega más campos si los necesitas
                  }))
                }
              : null
          };
        });
        
        this.jsonSearch = tones;
        this.initSearchInput();
      })
      .catch(err => {
        console.error('Error al obtener datos:', err);
      });      
  }

  /**
   * Procesa y guarda los datos JSON de tonos obtenidos del servidor.
   * @param {string} html - HTML que contiene los datos de tonos en formato JSON
   */
  processDataSearchTone(html) {
    if (!html?.trim()) {
      console.warn('Respuesta vacía al obtener datos de tonos');
      return;
    }

    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const jsonElement = doc.querySelector('#tones-colors-json');

      if (!jsonElement) {
        console.warn('No se encontró el elemento con datos JSON de tonos');
        return;
      }
      
      this.jsonSearch = JSON.parse(jsonElement.innerHTML).tones;

      this.initSearchInput(); // Reinicializa el input por si los datos llegan después
    } catch (err) {
      console.error('Error al parsear tonos:', err);
    }
  }

  /**
   * Inicializa el campo de búsqueda de tonos con sistema de debounce.
   * Permite mostrar sugerencias inteligentes en base al código numérico ingresado por el usuario.
   * Incluye manejo especial para la tecla Enter que solo muestra la primera sugerencia.
   */
  initSearchInput() {
    // Referencias al input de búsqueda y al contenedor de sugerencias
    const input = this.querySelectorAll(`input[type="number"]`);

    if (!input) return;

    /**
     * Determina si se debe aplicar búsqueda avanzada.
     * La búsqueda avanzada ocurre cuando el valor es de 3 cifras, termina en '00' y empieza por un dígito del 1 al 9.
     * Ejemplo: '200', '300', ..., '900'
     * @param {string} value - Valor numérico ya limpiado
     * @returns {boolean}
     */
    const isAdvancedSearch = (value) => {
      return (
        value.length === 3 &&
        value.endsWith('00') &&
        /^[1-9]00$/.test(value)
      );
    };

    /**
     * Renderiza las sugerencias de tonos en el DOM.
     * @param {Array} matches - Lista de tonos coincidentes con el criterio de búsqueda
     */
    const renderSuggestions = (matches, suggestions) => {
      suggestions.innerHTML = '';

      matches.forEach((match) => {
        const { tone_code, label, color, collection } = match;
        const li = document.createElement('li');
        
        // Crear elemento con vista previa del color y datos del tono
        // li.innerHTML = `
        //   <span style="background:${color};width:18px;height:18px;display:inline-flex;border-radius:50%;border:1px solid #ccc;"></span>
        //   <strong>${tone_code}</strong> - ${label}
        // `;

        // new
        li.innerHTML = `
          <span class="tone-color" style="--tone-bg-color: ${color};" style="background:${color};width:18px;height:18px;display:inline-flex;border-radius:50%;border:1px solid #ccc;"></span>
          <span class="tone-label">${tone_code}</span>
        `;
        
        // Añadir evento de clic para actualizar los detalles con el tono seleccionado
        li.addEventListener('click', () => {
          this.updateDetails(match);
          suggestions.style.display = 'none'; // Ocultar sugerencias al hacer clic
          //input.blur(); // Quitar el foco del input para mejorar UX
        });
        
        suggestions.appendChild(li);
      });

      // Mostrar sugerencias
      suggestions.style.display = matches.length ? 'block' : 'none';
    };

    /**
     * Maneja el evento de tecla Enter en el campo de búsqueda.
     * Solo muestra la primera sugerencia cuando se presiona Enter, sin auto-completar.
     */
    input.forEach(input => {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const parent = e.target.closest('[data-input-container]');
          const suggestionItems = parent.querySelectorAll('[data-suggestions] li');
          if (suggestionItems.length > 0) {
            suggestionItems.forEach((li, index) => {
              li.style.display = index === 0 ? 'flex' : 'none';
            });
            e.preventDefault(); // evita submit accidental y navegación de página
          }
        }
      })
    });

    /**
     * Evento al escribir en el input: aplica debounce para evitar búsquedas innecesarias
     */
    input.forEach(input => {
      input.addEventListener('input', (e) => {
        if (!this.jsonSearch) return;

        const parent = e.currentTarget.closest('.c-tone-search__search__inner');
        // Paso 2: Buscar el hijo con el atributo dentro del padre
        const suggestions = parent ? parent.querySelector('[data-suggestions]') : null;

        if (!suggestions) return;

        // Limpiamos cualquier búsqueda anterior en espera
        clearTimeout(this.debounceTimer);

        // Ejecutamos la búsqueda tras un pequeño retraso (debounce)
        this.debounceTimer = setTimeout(() => {
          // Limpiamos el valor: solo números, máximo 3 cifras
          const raw = input.value;
          const value = raw.replace(/\D/g, '').slice(0, 3);
          input.value = value;
          suggestions.innerHTML = '';

          // Si el campo está vacío, ocultamos sugerencias
          if (!value) {
            suggestions.style.display = 'none';
            return;
          }

          const matches = this.jsonSearch.filter(({ tone_code, collection }) => {
            if (!tone_code || !collection || collection.products_count < 1) return false;
          
            const len = value.length;
          
            if (len === 1) {
              // Un dígito: familia completa (ej: '3' → 300–399)
              return tone_code.startsWith(value[0]);
            }
          
            if (len === 2) {
              if (value[1] === '0') {
                // Dos dígitos y termina en 0: familia (ej: '30' → 300–399)
                return tone_code.startsWith(value[0]);
              } else {
                // Dos dígitos normales (ej: '34' → 340–349)
                return tone_code.startsWith(value);
              }
            }
          
            if (len === 3) {
              if (value[1] === '0' && value[2] === '0') {
                // Tres dígitos tipo '300', '400' → familia completa
                return tone_code.startsWith(value[0]);
              } else {
                // Búsqueda exacta por 3 dígitos
                return tone_code.startsWith(value);
              }
            }
          
            return false;
          }).slice(0, 5);
          

          // Mostramos u ocultamos las sugerencias según si hay resultados
          if (matches.length) {
            renderSuggestions(matches, suggestions);
          } else {
            suggestions.innerHTML = `<li class="tone-search-no-results">${this.translateNoResults}</li>`;
            suggestions.style.display = 'block'; // Mostramos el contenedor con el mensaje
          }
        }, this.debounceTime);
      });
    });

    /**
     * Evento al perder foco: oculta las sugerencias tras breve retraso
     * El retraso permite hacer clic en una sugerencia antes de que desaparezcan
     */
    input.forEach(input => {
      input.addEventListener('blur', (e) => {
        const parent = e.currentTarget.closest('.c-tone-search__search__inner');
        // Paso 2: Buscar el hijo con el atributo dentro del padre
        const suggestions = parent ? parent.querySelector('[data-suggestions]') : null;

        if (!suggestions) return;
          // Usamos un timeout pequeño para permitir que se complete el evento click en la sugerencia
        this.blurTimer = setTimeout(() => {
          suggestions.style.display = 'none';
        }, 150);
      });
    });
    
    // Cancelamos el timeout si volvemos a enfocar el input
    input.forEach(input => {
      input.addEventListener('focus', (e) => {
        const parent = e.currentTarget.closest('.c-tone-search__search__inner');
        // Paso 2: Buscar el hijo con el atributo dentro del padre
        const suggestions = parent ? parent.querySelector('[data-suggestions]') : null;
        if (!suggestions) return;
        
        if (this.blurTimer) {
          clearTimeout(this.blurTimer);
        }
      });
    });

  }

  activeBoxSearch() {
    this.dataFamily.classList.remove('active');
    this.dataSearch.classList.add('active');
    this.dataTabEvent.classList.add('active');

    this.querySelectorAll('.c-navPills .c-navPills__item').forEach(item => {
      item.classList.remove('c-navPills__item--active');
    });
  }

  activeBoxFamily() {
    this.dataSearch.classList.remove('active');
    this.dataTabEvent.classList.remove('active');
    this.dataFamily.classList.add('active');
  }

  // funcion para convertir hexadecimal en rgb
  hexToRgba(hex, opacity) {
    // Elimina el signo # si está presente
    hex = hex.replace(/^#/, '');
  
    // Convierte el formato corto (#abc) al largo (#aabbcc)
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
  
    // Extrae los valores R, G, B
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
  
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // funcion para convertir hexadecimal en rgb
  hexToRgba(hex, opacity) {
    // Elimina el signo # si está presente
    hex = hex.replace(/^#/, '');
  
    // Convierte el formato corto (#abc) al largo (#aabbcc)
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
  
    // Extrae los valores R, G, B
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
  
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  /**
   * Actualiza los detalles del tono seleccionado en la interfaz.
   * @param {Object} match - Objeto con información del tono seleccionado
   */
  updateDetails(match) {
    const { tone_code, label, color, collection } = match;
    
    // Actualizar texto y enlaces
    this.querySelector('[data-code-event]').textContent = tone_code;
    this.querySelector('[data-subtitle-event]').textContent = collection?.subtitle;
    this.querySelector('[data-description-event]').textContent = collection?.description
    
    const el = this.querySelector('[data-url-event]');
    const elText = this.querySelector('[data-url-event-text]');

    const count = collection?.products?.length || 0;

    if (count === 0) {
      el.style.display = 'none';
    } else {
      el.href = count === 1 ? collection?.products[0].url : collection.url;
      elText.textContent = count === 1 ? this.translateSeeProduct : this.translateSeeAll;
      el.style.display = '';
    }
    
    // Actualizar lista de productos
    if(collection?.products?.length > 0) {
      this.querySelector('[data-product-event]').innerHTML = collection.products.slice(0, 4).map(product => {
        return `<li class="c-tone-search__grid__products__item"><a href="${product.url}"><img src="${product.image}" alt="${product.title}"></a></li>`;
      }).join('');
    }else{
      this.querySelector('[data-product-event]').innerHTML = '';
    }

    // Establecer el color base dinámicamente en el CSS
    if(this.querySelector(`#style-color-search-${this.sectionId}`)) {
      this.querySelector(`#style-color-search-${this.sectionId}`).textContent = `
        .search-container-${this.sectionId} {
          --search-base-color: ${color};
        }
      `;
    }

    // aplicar color actualizado cuando se cambie el color del resultado de busqueda
    this.style.setProperty('--results-box-bg-color-rgb', this.hexToRgba(color, 0.1))
    this.style.setProperty('--results-box-text-color', color)

    // Mostrar resultados
    this.querySelector('.search-results, [data-search-results]').style.display = 'inherit';

    // Actualizar contador de productos con texto gramatical correcto
    const productCount = collection?.products?.length || 0;
    const qtyElement = this.querySelector('[data-qty-event]');

    if (productCount === 0) {
      qtyElement.textContent = this.translateThisToneNoProducts;
    } else {
      const productText = productCount === 1 ? this.translateProduct : this.translateProducts;
      qtyElement.textContent = `${productCount} ${productText}`;
    }
    
    // Asegurar que el botón "Ver todos" sea accesible y tenga estado de foco adecuado
    const verTodosBtn = this.querySelector('[data-url-event]');
    if (verTodosBtn) {
      verTodosBtn.setAttribute('aria-label', `${this.translateSeeAllProductsTone} ${tone_code}`);
    }

    this.activeBoxSearch();
  }
}

// Definimos el custom element para usarlo como <tone-search-navigation>
customElements.define('tone-search-navigation', ToneSearchNavigation);

/**
 * ButtonOpener
 * Custom element for the "Notify Me" button.
 * Lee los datos del producto desde el atributo data-json y dispara el evento 'open-modal-event' al hacer click,
 * permitiendo que BackInStockModal reciba y muestre el modal con los datos correctos.
 */
class ButtonOpener extends HTMLElement {
  constructor() { super(); }

  connectedCallback() {
    this.addEventListener('click', () => {
      // Parseamos los datos del atributo data-json
      const json = this.getAttribute('data-json');
      if (!json) return;

      try {
        const detail = JSON.parse(json);

        // Disparamos un evento personalizado con los datos del producto
        this.dispatchEvent(new CustomEvent('open-modal-event', {
          bubbles: true,
          composed: true,
          detail
        }));
      } catch (err) {
        console.error('Error al parsear los datos del botón:', err);
      }
    });
  }
}
customElements.define('button-modal-bis', ButtonOpener);

/**
 * AlertModal
 * Custom element that displays global success or error messages in a modal.
 * Listens for 'alert-modal-event' dispatched from BackInStockModal (or other components).
 * Muestra el mensaje recibido según el tipo (éxito o error).
 */
class AlertModal extends HTMLElement {
  constructor() { super(); }

  connectedCallback() {
    this.closeBtn = this.querySelector('.alert-close');
    this.closeBtn?.addEventListener('click', () => this.closeAlertModal());
    document.addEventListener('keydown', e => e.key === 'Escape' && this.closeAlertModal());

    // Escucha eventos personalizados para abrir el modal
    document.addEventListener('alert-modal-event', e => this.openAlertModal(e));
  }

  openAlertModal(e) {
    const success = this.querySelector('.msg-success');
    const error = this.querySelector('.msg-error');

    this.classList.remove('success', 'error');

    success?.classList.add('hidden');
    error?.classList.add('hidden');
    this.classList.remove('hidden');

    if (e.detail.type === 'success') {
      this.classList.add('success');
      success?.classList.remove('hidden');
    } else {
      this.classList.add('error');
      error?.classList.remove('hidden');
    }
  }

  closeAlertModal() {
    this.classList.add('hidden');
  }
}
customElements.define('alert-modal', AlertModal);

/**
 * BackInStockModal
 * Custom element for the "Back in Stock" subscription modal with Klaviyo integration.
 * Handles modal UI, form submission, and Klaviyo API communication.
 * Envía mensajes de éxito/error al AlertModal mediante el evento 'alert-modal-event'.
 */
class BackInStockModal extends HTMLElement {
  constructor() { super(); }

  connectedCallback() {
    this.modal = this.querySelector('#backInStockOverlay, #backInStockModal');
    this.btnClose = this.querySelectorAll('[data-modal-close]');
    this.form = this.querySelector('#bis-Form');
    this.globalTitle = this.querySelector('#bis-title');
    this.globalImage = this.querySelector('#bis-image')
    this.detail = null;

    // Añadir event listeners a todos los botones de cierre
    this.btnClose.forEach(btn => {
      btn.addEventListener('click', () => this.closeBackInStockModal());
    });
    this.modal?.addEventListener('click', e => e.target === this.modal && this.closeBackInStockModal());
    document.addEventListener('keydown', e => e.key === 'Escape' && this.closeBackInStockModal());

    // Abre el modal con detalles desde un evento personalizado
    document.addEventListener('open-modal-event', e => {
      this.detail = e.detail;
      this.globalTitle.innerHTML = this.detail.title;
      if (this.detail.image) {
        this.globalImage.src = this.detail.image
      } else {
        this.globalImage.classList('hidden')
      }
      this.openBackInStockModal();
    });

    // Envía la suscripción al submit del formulario
    this.form?.addEventListener('submit', async e => {
      e.preventDefault();
      if (this.detail) {
        const email = e.target.email.value;
        const response = await this.klaviyoBackInStockSubscribe(email);
        this.handleResponse(response);
      }
    });
  }

  openBackInStockModal() {
    // this.modal.style.display = 'flex';
    this.modal.classList.add('modal-opening')
  }

  closeBackInStockModal() {
    this.querySelector('#alert-mod')?.classList.add('hidden');
    // this.modal.style.display = 'none';
    this.modal.classList.remove('modal-opening')

    this.detail = null;
    this.form?.reset();
  }

  // Lógica para enviar la suscripción a Klaviyo
  async klaviyoBackInStockSubscribe(email) {
    const { feed, variantId, apiRevision: revision, klaviyoId } = this.detail;
    const variantDataId = feed + variantId; //32740673224783

    if (!klaviyoId || !revision) {
      return { status: 'error', error: 'Faltan datos de configuración de Klaviyo.' };
    }

    const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${klaviyoId}`;
    const payload = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: { email }
            }
          },
          channels: ["EMAIL"]
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: variantDataId
            }
          }
        }
      }
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          revision,
          'content-type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 202) return { status: 'ok', data: null };

      const text = await response.text();
      const result = text ? JSON.parse(text) : null;

      if (!response.ok) return { status: 'error', error: result };
      return { status: 'ok', data: result };
    } catch (error) {
      return { status: 'error', error: error.message || error };
    }
  }

  successAlert() {
    document.dispatchEvent(new CustomEvent('alert-modal-event', { detail: { type: 'success' } }));
  }

  errorAlert() {
    document.dispatchEvent(new CustomEvent('alert-modal-event', { detail: { type: 'error' } }));
  }

  handleResponse(response) {
    response.status === 'ok' ? this.successAlert() : this.errorAlert();
  }
}
customElements.define('back-in-stock-modal', BackInStockModal);


// KLAVIYO NEWSLETTER
class NewsletterSignup extends HTMLElement {
  constructor() {
    super();

    this.formId = this.dataset.formId;
    this.customSource = this.dataset.customSource;
    this.utmBlocks = JSON.parse(this.dataset.utmBlocks);

    this.modalTexts = this.querySelectorAll('[data-modal-texts]')

    this.formSelector = `#signup-${this.formId}-form`;
    this.fieldsSelector = `#signup-${this.formId}-fields`;

    this.email = this.querySelector('[data-required-email]')
    this.checkbox = this.querySelector('[data-required-checkbox]')
    this.alertEmail = this.querySelector('[data-alert-email]')
    this.alertCheckbox = this.querySelector('[data-alert-checkbox]')

    this.reloadButton = this.querySelector('[data-reload-form]')

    document.addEventListener('DOMContentLoaded', () => {
      this.attachForm();
    });

    this.onSuccess = this.onSuccessHandler.bind(this);
  }

  attachForm() {

    let extra_properties = {
      '$source': this.customSource,
      'Market': window.market.handle,
      'Country code': window.country.isoCode,
      'Country name': window.country.name,
      'Language code': window.lang.isoCode,
      'Language name': window.lang.name,
    }

    KlaviyoSubscribe.attachToForms(this.formSelector, {
      hide_form_on_success: true,
      custom_success_message: true,
      extra_properties,
      success: this.onSuccess
    });
  }

  onSuccessHandler($forms) {
    if (!$forms.length) {
      return;
    }

    const date = new Date();
    const dateString = date.toISOString();
    localStorage.setItem("3ina_already_subscribed", dateString);

    if(this.modalTexts) {
      this.modalTexts.forEach((text)=> {
        text.style.display = 'none'
      })
    }

    const wrapper = $forms[0].querySelector(this.fieldsSelector);
    if (wrapper) {
      wrapper.style.display = 'none';
    }

    if (this.reloadButton) {
      this.reloadButton.addEventListener('click', () => {
        const wrapper = $forms[0].querySelector(this.fieldsSelector);
  
        if (wrapper) {
          wrapper.style.display = 'block';
        }
  
        this.querySelector('[data-success-message]').style.display = 'none'
      })
    }

    document.dispatchEvent(new CustomEvent("newsletter:signup", {
      detail: {
        email: $forms.length ? $forms[0]?.querySelector('[name="email"]')?.value : undefined,
        source: this.customSource
      }
    }));
  }

}
customElements.define('newsletter-signup', NewsletterSignup);

class AcceptsMarketing extends HTMLElement {
  constructor() {
    super();  

    this.checkbox = this.querySelector('[data-accepts-checkbox]');
    this.hiddenInput = this.querySelector('[data-accepts-hidden]');
    
    this.checkbox.addEventListener('change', () => {
      this.hiddenInput.value = this.checkbox.checked;
    })
  }
}

customElements.define('accepts-marketing', AcceptsMarketing);
/**
 * Componente web <go-back-button>
 * Muestra un botón "Volver" solo si el usuario proviene del mismo dominio.
 * Al hacer clic, navega a la página anterior usando window.history.back().
 * Ref: https://developer.mozilla.org/en-US/docs/Web/API/History/back
 */
class GoBackButton extends HTMLElement {
  constructor() { super(); }

  connectedCallback() {
    const referrer = document.referrer;

    if (referrer && referrer.indexOf(window.location.hostname) !== -1) {
      this.classList.remove('hidden');
    }

    this.addEventListener('click', () => {
      history.back()});
  }
}
customElements.define('go-back-button', GoBackButton);

